<template>
  <!-- 🔴 The console is an APP SHELL, not a document: the page itself never scrolls.
       The root fills `.v-main`'s content box exactly (`height: 100%` — see the style
       block for why that is a plain CSS job and not a measured one), the chrome below
       keeps its natural height, and the three columns take every remaining pixel and
       scroll INSIDE themselves. Before this, `.tpg-col` guessed the chrome at
       `calc(100vh - 280px)` while the real chrome is ~420 px, so the columns hung
       ~140 px below the fold and the page grew a SECOND scrollbar under the column
       ones — scrolling the outer one dragged the toolbar away and left a dead band
       under the short columns. -->
  <div class="tpg-page">
    <v-container fluid class="tpg-container">
      <v-skeleton-loader
        :loading="!flagNav"
        transition="scale-transition"
        max-height="64"
        type="table-heading"
      >
        <v-toolbar color="primary" class="header-title text-white" data-testid="tpg-toolbar">
          <v-toolbar-title class="title">Tax Playground V2</v-toolbar-title>
          <v-spacer />
          <!-- 🔴 The collapse toggle lives HERE, in 64 px of chrome that was already
               being paid for, so offering it costs the columns nothing. Inside the
               card it would have added a row of its own and eaten more than it saved. -->
          <v-btn
            variant="text"
            size="small"
            class="text-white mr-2"
            :prepend-icon="settingsOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'"
            :title="settingsOpen ? 'ยุบแถบตั้งค่า (คืนพื้นที่ให้คอลัมน์)' : 'แสดงแถบตั้งค่า'"
            data-testid="tpg-settings-toggle"
            @click="settingsOpen = !settingsOpen"
          >{{ settingsOpen ? 'ยุบแถบตั้งค่า' : 'แถบตั้งค่า' }}</v-btn>
          <div class="d-flex pr-4">
            <v-toolbar-title class="title">{{ today }}</v-toolbar-title>
          </div>
        </v-toolbar>
      </v-skeleton-loader>

      <!-- L3 — nothing configured yet. Above the control bar, on purpose. -->
      <v-alert
        v-if="noYears"
        type="warning"
        variant="tonal"
        class="mt-3"
        data-testid="tpg-no-years"
      >
        ยังไม่มีปีภาษี V2 ในระบบ — ต้องสร้างที่หน้า Tax Configuration V2 ก่อนจึงจะทดสอบได้
        <template #append>
          <v-btn size="small" variant="outlined" data-testid="tpg-goto-config" @click="openTaxConfig">
            ไปที่ Tax Configuration V2
          </v-btn>
        </template>
      </v-alert>

      <!-- ── P3 · control bar (never sticky — chrome must be able to scroll away) ── -->
      <v-card class="card2">
        <v-card-text>
          <!-- Collapsed, the control bar leaves this one line behind so the session
               it is driving is still on screen. The rows below are the SAME markup on
               every breakpoint — only their visibility changes, so there is no second
               copy to keep in sync. (Collapse used to be xs-only; in a shell those two
               rows are ~180 px of permanent chrome taken off the columns, so the
               toolbar toggle now offers it everywhere. Default is unchanged —
               `created()`: open from sm up, collapsed on xs.) -->
          <!-- 🔴 `v-if`, NOT `v-show`: Vuetify's `.d-flex` is `display: flex !important`,
               which the inline `display: none` of `v-show` cannot override — the line
               would stay on screen on top of the rows it is supposed to replace. -->
          <div v-if="!settingsOpen" class="d-flex align-center flex-wrap" data-testid="tpg-settings-summary">
            <v-icon size="16" class="mr-1 text-medium-emphasis">mdi-cog-outline</v-icon>
            <span class="text-caption text-medium-emphasis">{{ settingsSummary }}</span>
            <!-- A second way back, ON the thing it opens. The toolbar button is 400 px
                 away from the card and reads as page chrome, so a reader who collapsed
                 by accident looks for the way back here first. -->
            <v-btn
              icon="mdi-chevron-down"
              size="x-small"
              variant="text"
              class="ml-1"
              title="แสดงแถบตั้งค่า"
              aria-label="แสดงแถบตั้งค่า"
              data-testid="tpg-settings-expand"
              @click="settingsOpen = true"
            />
          </div>

          <v-row v-show="settingsOpen" align="start" dense>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                :model-value="store.userId"
                label="CIF / UserId (ทดสอบ)"
                density="compact"
                variant="outlined"
                hide-details="auto"
                :error-messages="userIdError ? [userIdError] : []"
                data-testid="tpg-userid"
                @update:model-value="s.setUserId($event)"
              />
              <div class="text-caption text-medium-emphasis mt-1" data-testid="tpg-userid-hint">
                ใช้ CIF ทดสอบเท่านั้น — ห้ามใส่ CIF จริงของลูกค้า
              </div>
            </v-col>

            <v-col cols="12" sm="6" md="3">
              <v-text-field
                :model-value="store.sessionId"
                label="SessionId"
                density="compact"
                variant="outlined"
                hide-details="auto"
                data-testid="tpg-sessionid"
                @update:model-value="s.setSessionId($event)"
              />
            </v-col>

            <v-col cols="12" sm="6" md="3" class="d-flex flex-wrap ga-2 align-center">
              <v-btn
                variant="outlined"
                size="small"
                prepend-icon="mdi-refresh"
                data-testid="tpg-new-session"
                @click="onNewSession"
              >สุ่ม session ใหม่</v-btn>
              <v-btn
                variant="outlined"
                size="small"
                prepend-icon="mdi-database-search-outline"
                :loading="store.peeking"
                :disabled="!canAct"
                data-testid="tpg-get-state"
                @click="onGetState"
              >Get Tax State</v-btn>
            </v-col>

            <v-col cols="12" sm="6" md="3" class="d-flex justify-md-end align-center ga-2">
              <v-btn
                variant="outlined"
                size="small"
                prepend-icon="mdi-tray-arrow-down"
                :loading="store.snapshotBusy"
                :disabled="!canAct"
                data-testid="tpg-export"
                title="ดาวน์โหลดสถานะฟอร์มปัจจุบันเป็นไฟล์ JSON"
                @click="onExportForm"
              >Export</v-btn>
              <v-btn
                variant="outlined"
                size="small"
                prepend-icon="mdi-tray-arrow-up"
                :disabled="!canAct"
                data-testid="tpg-import"
                title="โหลดไฟล์ snapshot เพื่อข้ามบทสนทนาไปยังจุดนั้น"
                @click="openImportDialog"
              >Import</v-btn>
              <v-btn
                variant="outlined"
                size="small"
                color="warning"
                prepend-icon="mdi-restart"
                :disabled="!canAct"
                data-testid="tpg-reset"
                @click="openResetDialog"
              >Reset / ฟอร์มใหม่</v-btn>
            </v-col>
          </v-row>

          <v-row v-show="settingsOpen" align="center" dense class="mt-1">
            <v-col cols="12" sm="6" md="3">
              <v-select
                :items="yearItems"
                :model-value="store.yearId"
                label="ปีภาษี"
                density="compact"
                variant="outlined"
                hide-details="auto"
                :loading="store.optionsLoading"
                :disabled="store.optionsLoading"
                data-testid="tpg-year"
                @update:model-value="onVersionChange('yearId', $event)"
              />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-select
                :items="configVersionItems"
                :model-value="store.configVersionId"
                label="Config Version"
                density="compact"
                variant="outlined"
                hide-details="auto"
                :loading="store.optionsLoading"
                :disabled="store.optionsLoading"
                data-testid="tpg-configversion"
                @update:model-value="onVersionChange('configVersionId', $event)"
              />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-select
                :items="questionVersionItems"
                :model-value="store.questionVersionId"
                label="Question Version"
                density="compact"
                variant="outlined"
                hide-details="auto"
                :loading="store.optionsLoading"
                :disabled="store.optionsLoading"
                data-testid="tpg-questionversion"
                @update:model-value="onVersionChange('questionVersionId', $event)"
              />
            </v-col>
            <!-- 🔴 D5 — this select REPLACED the old "Model" picker. Every LLM knob
                 (provider/model/temperature/…) and every node prompt now lives in a
                 tax.TaxPlanningSettingsV2 VERSION, so picking a version is the only way
                 the console and the flow node can be guaranteed to run the same config.
                 NOT wired to `onVersionChange`: see `onSettingsVersionChange`. -->
            <v-col cols="12" sm="6" md="3">
              <v-select
                :items="settingsVersionItems"
                :model-value="store.settingsVersionId"
                label="Settings Version"
                density="compact"
                variant="outlined"
                hide-details="auto"
                :hint="settingsVersionHint"
                persistent-hint
                :loading="store.optionsLoading"
                :disabled="store.optionsLoading || !settingsVersionItems.length"
                data-testid="tpg-settingsversion"
                @update:model-value="onSettingsVersionChange($event)"
              >
                <template #item="{ props: itemProps, item }">
                  <v-list-item v-bind="itemProps" :subtitle="item.raw.description || null">
                    <template v-if="item.raw.isActive" #append>
                      <v-chip size="x-small" color="success" variant="tonal">ใช้งานอยู่</v-chip>
                    </template>
                  </v-list-item>
                </template>
                <template #selection="{ item }">
                  <span class="text-truncate">{{ item.title }}</span>
                  <v-chip
                    v-if="item.raw && item.raw.isActive"
                    size="x-small"
                    color="success"
                    variant="tonal"
                    class="ml-2"
                    data-testid="tpg-settingsversion-active"
                  >ใช้งานอยู่</v-chip>
                </template>
              </v-select>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- ── L2 · first run — the three columns stay HIDDEN until there is a session ── -->
      <v-card v-if="!hasSession" class="mx-auto mt-6" max-width="560" data-testid="tpg-first-run">
        <v-card-title class="text-subtitle-1">เริ่มทดสอบชุดคำถามใน 3 ขั้น</v-card-title>
        <v-card-text>
          <v-list density="compact">
            <v-list-item>1. ใส่ CIF ทดสอบ (ขึ้นต้น PG-)</v-list-item>
            <v-list-item>2. เลือกปีภาษี / เวอร์ชันคอนฟิก / ชุดคำถาม (เลือก draft ได้)</v-list-item>
            <v-list-item>3. พิมพ์ข้อความแรกแล้วกดส่ง</v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn
            color="primary"
            variant="flat"
            rounded="lg"
            prepend-icon="mdi-refresh"
            data-testid="tpg-first-run-new-session"
            @click="onNewSession"
          >สุ่ม session ใหม่</v-btn>
        </v-card-actions>
      </v-card>

      <template v-else>
        <!-- ── T12 · the "Get Tax State" peek ──────────────────────────────
             There is no card here any more. The peek's MESSAGE is pushed into the
             chat as an ordinary bubble (`side: 'state'`, see `bubbles()`), its
             kind / form id ride along as that bubble's `meta` caption, and its
             chips were always already rendered by the composer's own quick-reply
             row (`quickReply()` falls back to `lastState`). A tonal card above the
             conversation restating all four was the confusing part. -->

        <!-- Tablet (sm): the timeline becomes a horizontal strip under the control bar. -->
        <div v-if="smOnly" class="tpg-strip mt-3" data-testid="tpg-turn-strip">
          <div
            v-for="row in timelineDesc"
            :key="row.t.turnId"
            class="tpg-strip-card"
            :class="{ 'tpg-turn--active': isActiveTurn(row.t.turnId) }"
            :data-testid="'tpg-turn-card-' + row.t.turnId"
            @click="onOpenTurn(row.t.turnId)"
          >
            <div class="text-caption font-weight-medium">#{{ row.t.turnNo }} · {{ fmtTime(row.t.createdDate) }}</div>
            <div class="text-caption text-truncate">{{ row.t.askedFieldKey || '—' }}</div>
            <v-chip
              v-if="s.isRepeatTurn(row.i)"
              size="x-small"
              color="orange"
              variant="tonal"
              prepend-icon="mdi-refresh"
              :title="repeatTitle(row.t)"
              :data-testid="'tpg-repeat-chip-' + row.t.turnId"
            >⟳ ถามซ้ำ</v-chip>
          </div>
        </div>

        <!-- Mobile (xs): a single column behind sticky tabs. -->
        <div v-if="isXs" class="mt-3 tpg-xs-pane">
          <v-tabs
            :model-value="activeTab"
            density="compact"
            show-arrows
            class="tpg-mobile-tabs"
            @update:model-value="inspectorTab = $event"
          >
            <v-tab
              v-for="t in tabDefs"
              :key="t.value"
              :value="t.value"
              :color="t.color"
              :data-testid="'tpg-tab-' + t.value"
            >
              <v-icon v-if="t.icon" size="14" class="mr-1">{{ t.icon }}</v-icon>
              {{ t.label }}
            </v-tab>
          </v-tabs>

          <v-window :model-value="activeTab" class="mt-2">
            <v-window-item value="chat">
              <div class="tpg-strip mb-2" data-testid="tpg-turn-strip-xs">
                <div
                  v-for="row in timelineDesc"
                  :key="row.t.turnId"
                  class="tpg-strip-card"
                  :class="{ 'tpg-turn--active': isActiveTurn(row.t.turnId) }"
                  :data-testid="'tpg-turn-card-xs-' + row.t.turnId"
                  @click="onOpenTurn(row.t.turnId)"
                >
                  <div class="text-caption font-weight-medium">#{{ row.t.turnNo }}</div>
                  <div class="text-caption text-truncate">{{ row.t.askedFieldKey || '—' }}</div>
                </div>
              </div>
              <TaxPlaygroundChatPanelV2
                :bubbles="bubbles"
                :quick-reply="quickReply"
                :quick-reply-source="quickReplySource"
                :sending="store.sending"
                :elapsed-sec="elapsedSec"
                :can-send="canSend"
                :form-status="formStatus"
                :form-id="formId"
                :version-abandoned-form-id="versionAbandonedFormId"
                @send="onSend"
                @quick-reply="onQuickReply"
                @card-submit="onCardSubmit"
                @new-form="openResetDialog"
                @view-tax-json="inspectorTab = 'json'"
                @select-turn="onOpenTurn"
              />
            </v-window-item>

            <!-- 🔴 The form tab body is BYTE-IDENTICAL to the one in the tablet/desktop
                 branch below — including the two dynamic testids. Keep them in step. -->
            <v-window-item value="form">
              <div class="tpg-form-pane">
                <div v-if="turnNotFound" class="text-medium-emphasis text-caption pa-2">
                  ไม่พบเทิร์นนี้ — อาจถูกลบด้วย Reset หรือเป็นของบริษัทอื่น
                </div>
                <TaxPlaygroundConfigFormPanelV2
                  v-else-if="configForm"
                  :config-form="configForm"
                  :changed-paths="changedPaths"
                  @copy="onCopy"
                />
                <v-alert
                  v-else-if="configFormTruncated"
                  type="warning"
                  density="compact"
                  variant="tonal"
                  :data-testid="cfTruncatedTestId"
                >{{ configFormTruncatedNote }}</v-alert>
                <div v-else class="text-medium-emphasis text-caption pa-2" :data-testid="cfEmptyTestId">
                  กด "Get Tax State" หรือส่งข้อความ เพื่อดูสถานะฟอร์ม
                </div>
              </div>
            </v-window-item>

            <v-window-item value="llm">
              <v-alert
                v-if="llmCallsTruncated"
                type="warning"
                density="compact"
                variant="tonal"
                class="mb-2"
                data-testid="tpg-llm-truncated"
              >{{ llmCallsTruncatedNote }}</v-alert>
              <TaxPlaygroundPromptViewerV2
                :llm-calls="llmCalls"
                :askable-count="askableCount"
                :asked-field-key="askedFieldKey"
                :user-message="turnUserMessage"
                @open-edit="openEdit"
                @copy="onCopy"
              />
            </v-window-item>

            <v-window-item value="json">
              <div class="tpg-json-pane">
                <v-alert
                  v-if="taxStateTruncated"
                  type="warning"
                  density="compact"
                  variant="tonal"
                  class="mb-2"
                  data-testid="tpg-json-truncated"
                >{{ taxStateTruncatedNote }}</v-alert>
                <v-alert
                  v-else-if="taxJsonTooBig"
                  type="info"
                  density="compact"
                  variant="tonal"
                  class="mb-2"
                  data-testid="tpg-json-large"
                >
                  JSON ก้อนนี้ใหญ่ ({{ taxJsonKb }} KB) — แสดงแบบย่อเพื่อไม่ให้หน้าค้าง
                  <template #append>
                    <v-btn size="x-small" variant="text" data-testid="tpg-json-showall" @click="taxJsonShowAll = true">
                      แสดงทั้งหมด
                    </v-btn>
                  </template>
                </v-alert>
                <div class="d-flex justify-end">
                  <v-btn
                    icon="mdi-content-copy"
                    size="x-small"
                    variant="text"
                    title="คัดลอก Tax JSON"
                    aria-label="คัดลอก Tax JSON"
                    data-testid="tpg-json-copy"
                    @click="onCopy({ text: taxStateText, label: 'Tax JSON' })"
                  />
                </div>
                <pre class="code-block" data-testid="tpg-tax-json">{{ taxStateText }}</pre>
              </div>
            </v-window-item>

            <!-- 🔴 The flow tab body is BYTE-IDENTICAL to the one in the tablet/desktop
                 branch below (indentation aside). Keep the two copies in step. -->
            <v-window-item value="flow">
              <div data-testid="tpg-flow-pane">
                <TaxTurnFlowPanelV2
                  :flow="turnFlow"
                  :loading="store.loadingTurn"
                  :turn-selected="turnSelected"
                  :trace-text="traceText"
                  source="playground"
                  @copy="onCopy"
                />
              </div>
            </v-window-item>

            <v-window-item value="trace">
              <div class="d-flex justify-end">
                <v-btn
                  icon="mdi-content-copy"
                  size="x-small"
                  variant="text"
                  title="คัดลอก trace"
                  aria-label="คัดลอก trace"
                  data-testid="tpg-trace-copy"
                  @click="onCopy({ text: traceText, label: 'Trace' })"
                />
              </div>
              <pre class="code-block" data-testid="tpg-trace">{{ traceText }}</pre>
            </v-window-item>
          </v-window>
        </div>

        <!-- Tablet + Desktop -->
        <v-row v-else no-gutters class="mt-3 tpg-row">
          <!-- ── A · turn timeline (desktop only, collapsible to a rail) ── -->
          <v-col
            v-if="mdAndUp && !inspectorFull"
            :cols="colSpans.timeline.cols"
            :md="colSpans.timeline.md"
            :lg="colSpans.timeline.lg"
            class="tpg-col pr-2"
            :class="{ 'tpg-col--rail': timelineCollapsed }"
            role="region"
            aria-label="ไทม์ไลน์เทิร์น"
          >
            <!-- Collapsed: a narrow rail that keeps the way back visible. The column
                 is never removed outright, so the user can always re-open it from
                 where it used to be. -->
            <div v-if="timelineCollapsed" class="tpg-rail-inner">
              <v-btn
                icon="mdi-chevron-right"
                size="x-small"
                variant="text"
                title="แสดงคอลัมน์ไทม์ไลน์"
                aria-label="แสดงคอลัมน์ไทม์ไลน์"
                data-testid="tpg-timeline-expand"
                @click="setTimelineCollapsed(false)"
              />
              <div class="tpg-rail-label">เทิร์น ({{ store.turnsTotal }})</div>
            </div>

            <v-skeleton-loader v-else-if="store.optionsLoading" type="paragraph" data-testid="tpg-col-skeleton" />
            <template v-else>
              <div class="d-flex align-center mb-2">
                <span class="text-subtitle-2">เทิร์น ({{ store.turnsTotal }})</span>
                <v-spacer />
                <v-btn
                  icon="mdi-refresh"
                  size="x-small"
                  variant="text"
                  title="โหลดไทม์ไลน์ใหม่"
                  aria-label="โหลดไทม์ไลน์ใหม่"
                  :loading="store.loadingTurns"
                  data-testid="tpg-timeline-refresh"
                  @click="onRefreshTurns"
                />
                <v-btn
                  icon="mdi-chevron-left"
                  size="x-small"
                  variant="text"
                  title="ยุบคอลัมน์ไทม์ไลน์ (คืนพื้นที่ให้ฝั่งขวา)"
                  aria-label="ยุบคอลัมน์ไทม์ไลน์"
                  data-testid="tpg-timeline-collapse"
                  @click="setTimelineCollapsed(true)"
                />
              </div>

              <v-alert
                v-if="repeatStreak >= 2"
                type="warning"
                variant="tonal"
                density="compact"
                class="mb-2"
                data-testid="tpg-repeat-alert"
              >⟳ ถามข้อเดิม {{ repeatStreak + 1 }} เทิร์นติดกัน — ฟอร์มอาจค้าง</v-alert>

              <!-- L5 (จ) — ghost card while a turn is in flight. -->
              <v-card v-if="store.sending" class="mb-2" variant="tonal" data-testid="tpg-timeline-ghost">
                <v-card-text class="py-2 px-3 text-caption">
                  #{{ (store.turnsTotal || 0) + 1 }} กำลังส่ง…
                  <v-skeleton-loader type="text" />
                </v-card-text>
              </v-card>

              <div
                v-if="!store.turns.length"
                class="text-medium-emphasis text-caption pa-2 text-center"
                :data-testid="turnNotFound ? 'tpg-timeline-gone' : 'tpg-timeline-empty'"
              >
                <v-icon size="28" color="grey-lighten-1">mdi-history</v-icon>
                <div class="mt-1">{{ turnNotFound ? 'session นี้ไม่มีเทิร์นแล้ว' : 'ยังไม่มีเทิร์นใน session นี้' }}</div>
              </div>

              <v-card
                v-for="row in timelineDesc"
                :key="row.t.turnId"
                class="mb-2 tpg-turn-card"
                :class="{ 'tpg-turn--active': isActiveTurn(row.t.turnId) }"
                density="compact"
                variant="outlined"
                :data-testid="'tpg-turn-card-' + row.t.turnId"
                @click="onOpenTurn(row.t.turnId)"
              >
                <v-card-text class="py-2 px-3">
                  <div class="d-flex align-center">
                    <span class="text-caption font-weight-medium">#{{ row.t.turnNo }}</span>
                    <span class="text-caption text-medium-emphasis ml-2">{{ fmtTime(row.t.createdDate) }}</span>
                    <v-spacer />
                    <span class="text-caption text-medium-emphasis">{{ row.t.llmCallCount ?? 0 }} call</span>
                  </div>
                  <div class="text-caption text-truncate">{{ row.t.askedFieldKey || '—' }}</div>
                  <div class="d-flex flex-wrap ga-1 mt-1">
                    <v-chip
                      v-if="s.isRepeatTurn(row.i)"
                      size="x-small"
                      color="orange"
                      variant="tonal"
                      prepend-icon="mdi-refresh"
                      :title="repeatTitle(row.t)"
                      :data-testid="'tpg-repeat-chip-' + row.t.turnId"
                    >⟳ ถามซ้ำ</v-chip>
                    <v-chip
                      v-if="(row.t.changedCount ?? 0) > 0"
                      size="x-small"
                      color="amber-darken-2"
                      variant="outlined"
                      :title="row.t.changedCount + ' ค่าเปลี่ยนในเทิร์นนี้'"
                    >{{ row.t.changedCount }} เปลี่ยน</v-chip>
                  </div>
                </v-card-text>
              </v-card>
            </template>
          </v-col>

          <!-- ── B · chat (hidden while the inspector is full-width) ────── -->
          <v-col
            v-if="!inspectorFull"
            :cols="colSpans.chat.cols"
            :sm="colSpans.chat.sm"
            :md="colSpans.chat.md"
            :lg="colSpans.chat.lg"
            class="tpg-col px-2"
            role="region"
            aria-label="แชททดสอบ"
          >
            <v-skeleton-loader v-if="store.optionsLoading" type="paragraph" />
            <TaxPlaygroundChatPanelV2
              v-else
              :bubbles="bubbles"
              :quick-reply="quickReply"
              :quick-reply-source="quickReplySource"
              :sending="store.sending"
              :elapsed-sec="elapsedSec"
              :can-send="canSend"
              :form-status="formStatus"
              :form-id="formId"
              :version-abandoned-form-id="versionAbandonedFormId"
              @send="onSend"
              @quick-reply="onQuickReply"
              @card-submit="onCardSubmit"
              @new-form="openResetDialog"
              @view-tax-json="inspectorTab = 'json'"
              @select-turn="onOpenTurn"
            />
          </v-col>

          <!-- ── C · inspector ─────────────────────────────────────────── -->
          <v-col
            :cols="colSpans.inspector.cols"
            :sm="colSpans.inspector.sm"
            :md="colSpans.inspector.md"
            :lg="colSpans.inspector.lg"
            class="tpg-col tpg-inspector"
            :class="{ 'tpg-inspector--waiting': store.sending, 'pl-2': !inspectorFull }"
            role="region"
            aria-label="ตัวตรวจสอบเทิร์น"
          >
            <div class="tpg-inspector-head d-flex align-center flex-wrap ga-2">
              <span v-if="store.sending" class="d-flex align-center">
                <v-chip size="x-small" color="grey" variant="tonal" data-testid="tpg-inspector-waiting">
                  กำลังรอเทิร์นใหม่…
                </v-chip>
              </span>
              <template v-else>
                <span class="text-caption">
                  <template v-if="activeTurn">กำลังดูเทิร์น #{{ activeTurn.turnNo }} · {{ fmtTime(activeTurn.createdDate) }}</template>
                  <template v-else>ยังไม่ได้เลือกเทิร์น</template>
                </span>
                <v-chip
                  v-if="activeTurnIsRepeat"
                  size="x-small"
                  color="orange"
                  variant="tonal"
                  prepend-icon="mdi-refresh"
                  :title="activeRepeatTitle"
                  data-testid="tpg-repeat-chip-active"
                >⟳ ถามซ้ำ</v-chip>
              </template>
              <!-- The spacer and the two buttons sit OUTSIDE the sending/idle
                   branches so the width toggle keeps its place mid-turn instead of
                   popping in and out. (The whole panel is inert while sending —
                   `.tpg-inspector--waiting` — which is pre-existing behaviour.) -->
              <v-spacer />
              <v-btn
                v-if="!store.sending && activeTurn"
                size="x-small"
                variant="text"
                prepend-icon="mdi-link-variant"
                data-testid="tpg-copy-turn-link"
                @click="copyTurnLink"
              >คัดลอกลิงก์เทิร์นนี้</v-btn>
              <v-btn
                size="x-small"
                variant="text"
                :prepend-icon="inspectorFull ? 'mdi-arrow-collapse-horizontal' : 'mdi-arrow-expand-horizontal'"
                :title="inspectorFull ? 'กลับไปแบบหลายคอลัมน์' : 'ขยายแผงนี้เต็มความกว้าง (ซ่อนไทม์ไลน์+แชท)'"
                data-testid="tpg-inspector-full"
                @click="toggleInspectorFull"
              >{{ inspectorFull ? 'ย่อกลับ' : 'เต็มความกว้าง' }}</v-btn>
            </div>

            <!-- L15 — a dead deep link is an EMPTY STATE, never a red error. -->
            <div v-if="turnNotFound" class="text-center pa-6" data-testid="tpg-turn-notfound">
              <v-icon size="40" color="grey-lighten-1">mdi-link-variant-off</v-icon>
              <div class="mt-2 text-body-2">ไม่พบเทิร์นนี้ — อาจถูกลบด้วย Reset หรือเป็นของบริษัทอื่น</div>
              <div class="d-flex justify-center flex-wrap ga-2 mt-3">
                <v-btn
                  v-if="store.turns.length"
                  size="small"
                  variant="outlined"
                  data-testid="tpg-turn-notfound-session"
                  @click="openSessionFromStart"
                >เปิด session นี้ตั้งแต่ต้น</v-btn>
                <v-btn
                  size="small"
                  variant="outlined"
                  prepend-icon="mdi-refresh"
                  data-testid="tpg-turn-notfound-new"
                  @click="onNewSession"
                >สุ่ม session ใหม่</v-btn>
              </div>
            </div>

            <template v-else>
              <!-- P0/P1 (as-built) — the Form Template is the FIRST TAB on every
                   breakpoint, never a pinned half-column. Pinning it on desktop cost
                   both halves their height: the form and the tab below it were each
                   too short to read. One tab strip owns the whole column now. -->
              <div class="tpg-pane">
                <v-tabs
                  :model-value="activeTab"
                  density="compact"
                  show-arrows
                  class="tpg-tabs"
                  @update:model-value="inspectorTab = $event"
                >
                  <v-tab
                    v-for="t in tabDefs"
                    :key="t.value"
                    :value="t.value"
                    :color="t.color"
                    :data-testid="'tpg-tab-' + t.value"
                  >
                    <v-icon v-if="t.icon" size="14" class="mr-1">{{ t.icon }}</v-icon>
                    {{ t.label }}
                  </v-tab>
                </v-tabs>

                <v-window :model-value="activeTab" class="mt-2">
                  <!-- 🔴 Same body as the xs branch above (`turnNotFound` can never be
                       true here — the outer `v-if` already caught it — but keeping the
                       branch makes the two copies literally identical). -->
                  <v-window-item value="form">
                    <div class="tpg-form-pane">
                      <div v-if="turnNotFound" class="text-medium-emphasis text-caption pa-2">
                        ไม่พบเทิร์นนี้ — อาจถูกลบด้วย Reset หรือเป็นของบริษัทอื่น
                      </div>
                      <TaxPlaygroundConfigFormPanelV2
                        v-else-if="configForm"
                        :config-form="configForm"
                        :changed-paths="changedPaths"
                        @copy="onCopy"
                      />
                      <v-alert
                        v-else-if="configFormTruncated"
                        type="warning"
                        density="compact"
                        variant="tonal"
                        :data-testid="cfTruncatedTestId"
                      >{{ configFormTruncatedNote }}</v-alert>
                      <div v-else class="text-medium-emphasis text-caption pa-2" :data-testid="cfEmptyTestId">
                        กด "Get Tax State" หรือส่งข้อความ เพื่อดูสถานะฟอร์ม
                      </div>
                    </div>
                  </v-window-item>

                  <v-window-item value="llm">
                    <v-alert
                      v-if="llmCallsTruncated"
                      type="warning"
                      density="compact"
                      variant="tonal"
                      class="mb-2"
                      data-testid="tpg-llm-truncated"
                    >{{ llmCallsTruncatedNote }}</v-alert>
                    <TaxPlaygroundPromptViewerV2
                      :llm-calls="llmCalls"
                      :askable-count="askableCount"
                      :asked-field-key="askedFieldKey"
                      :user-message="turnUserMessage"
                      @open-edit="openEdit"
                      @copy="onCopy"
                    />
                  </v-window-item>

                  <v-window-item value="json">
                    <v-alert
                      v-if="taxStateTruncated"
                      type="warning"
                      density="compact"
                      variant="tonal"
                      class="mb-2"
                      data-testid="tpg-json-truncated"
                    >{{ taxStateTruncatedNote }}</v-alert>
                    <v-alert
                      v-else-if="taxJsonTooBig"
                      type="info"
                      density="compact"
                      variant="tonal"
                      class="mb-2"
                      data-testid="tpg-json-large"
                    >
                      JSON ก้อนนี้ใหญ่ ({{ taxJsonKb }} KB) — แสดงแบบย่อเพื่อไม่ให้หน้าค้าง
                      <template #append>
                        <v-btn size="x-small" variant="text" data-testid="tpg-json-showall" @click="taxJsonShowAll = true">
                          แสดงทั้งหมด
                        </v-btn>
                      </template>
                    </v-alert>
                    <div class="d-flex justify-end">
                      <v-btn
                        icon="mdi-content-copy"
                        size="x-small"
                        variant="text"
                        title="คัดลอก Tax JSON"
                        aria-label="คัดลอก Tax JSON"
                        data-testid="tpg-json-copy"
                        @click="onCopy({ text: taxStateText, label: 'Tax JSON' })"
                      />
                    </div>
                    <pre class="code-block" data-testid="tpg-tax-json">{{ taxStateText }}</pre>
                  </v-window-item>

                  <!-- 🔴 Same body as the flow tab in the xs branch above — the panel
                       scrolls inside itself (max-height 52vh) on both, so neither copy
                       needs a scrolling wrapper of its own. -->
                  <v-window-item value="flow">
                    <div data-testid="tpg-flow-pane">
                      <TaxTurnFlowPanelV2
                        :flow="turnFlow"
                        :loading="store.loadingTurn"
                        :turn-selected="turnSelected"
                        :trace-text="traceText"
                        source="playground"
                        @copy="onCopy"
                      />
                    </div>
                  </v-window-item>

                  <v-window-item value="trace">
                    <div class="d-flex justify-end">
                      <v-btn
                        icon="mdi-content-copy"
                        size="x-small"
                        variant="text"
                        title="คัดลอก trace"
                        aria-label="คัดลอก trace"
                        data-testid="tpg-trace-copy"
                        @click="onCopy({ text: traceText, label: 'Trace' })"
                      />
                    </div>
                    <pre class="code-block" data-testid="tpg-trace">{{ traceText }}</pre>
                  </v-window-item>
                </v-window>
              </div>
            </template>
          </v-col>
        </v-row>
      </template>

      <!-- ── L9 · confirm BEFORE the version actually changes ───────────── -->
      <v-dialog v-model="versionSwitchDialog" max-width="560" persistent data-testid="tpg-version-dialog">
        <v-card>
          <v-card-title class="bg-warning text-white">
            <v-icon class="mr-2">mdi-alert</v-icon>
            เปลี่ยนเวอร์ชันกลางคัน
          </v-card-title>
          <v-card-text class="pt-4">
            <p>session นี้กำลังตอบฟอร์ม #{{ formId }} อยู่ — การเปลี่ยนเวอร์ชันจะทิ้งฟอร์มเดิม (Abandoned) แล้วเริ่มถามใหม่ตั้งแต่ข้อแรก</p>
            <p class="text-caption text-medium-emphasis mt-2">เทิร์นเก่าใน timeline ยังอยู่ครบ ใช้เทียบก่อน/หลังได้</p>
          </v-card-text>
          <v-card-actions class="pa-4">
            <v-spacer />
            <v-btn variant="outlined" data-testid="tpg-version-cancel" @click="cancelVersionSwitch">ยกเลิก</v-btn>
            <v-btn color="warning" variant="elevated" data-testid="tpg-version-confirm" @click="confirmVersionSwitch">
              เปลี่ยนและเริ่มใหม่
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- ── L10 · confirm reset ────────────────────────────────────────── -->
      <v-dialog v-model="resetDialog" max-width="520" persistent data-testid="tpg-reset-dialog">
        <v-card>
          <v-card-title class="bg-warning text-white">
            <v-icon class="mr-2">mdi-restart</v-icon>
            ยืนยันการเริ่มฟอร์มใหม่
          </v-card-title>
          <v-card-text class="pt-4">
            <p>จะทิ้งฟอร์มปัจจุบัน (#{{ formId }}) แล้วเริ่มถามใหม่ตั้งแต่ข้อแรก — ข้อมูลฟอร์มเดิมยังเก็บไว้ในระบบ (สถานะ Abandoned) ไม่ได้ถูกลบ</p>
            <v-checkbox
              v-model="clearTurnsChecked"
              density="compact"
              hide-details
              :label="'ลบประวัติเทิร์นของ session นี้ด้วย (' + store.turns.length + ' เทิร์น)'"
              data-testid="tpg-reset-clearturns"
            />
            <p v-if="clearTurnsChecked" class="text-error text-caption mt-1">ลบแล้วกู้คืนไม่ได้</p>
          </v-card-text>
          <v-card-actions class="pa-4">
            <v-spacer />
            <v-btn variant="outlined" data-testid="tpg-reset-cancel" @click="resetDialog = false">ยกเลิก</v-btn>
            <v-btn
              :color="clearTurnsChecked ? 'error' : 'warning'"
              variant="elevated"
              :loading="store.resetting"
              data-testid="tpg-reset-confirm"
              @click="confirmReset"
            >เริ่มฟอร์มใหม่</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- ── snapshot import — fast-forward the session to an exported point ── -->
      <v-dialog v-model="importDialog" max-width="640" persistent scrollable data-testid="tpg-import-dialog">
        <v-card>
          <v-card-title class="bg-primary text-white">
            <v-icon class="mr-2">mdi-tray-arrow-up</v-icon>
            Import snapshot — ข้ามไปยังจุดที่บันทึกไว้
          </v-card-title>
          <v-card-text class="pt-4">
            <p class="mb-3">
              จะทิ้งฟอร์มปัจจุบัน{{ formId ? ` (#${formId})` : '' }} แล้วสร้างฟอร์มใหม่จาก snapshot —
              เทิร์นถัดไปจะคุยต่อจากจุดที่ export ไว้
            </p>

            <v-btn
              variant="outlined"
              size="small"
              prepend-icon="mdi-file-upload-outline"
              data-testid="tpg-import-pick"
              @click="pickImportFile"
            >เลือกไฟล์ .json</v-btn>
            <input
              ref="importFile"
              type="file"
              accept="application/json,.json"
              class="d-none"
              data-testid="tpg-import-file"
              @change="onImportFileChosen"
            >

            <v-textarea
              v-model="importText"
              class="mt-3"
              label="หรือวาง JSON ที่นี่"
              rows="6"
              variant="outlined"
              density="compact"
              hide-details
              data-testid="tpg-import-text"
            />

            <v-alert
              v-if="importPreview"
              type="info"
              variant="tonal"
              density="comfortable"
              class="mt-3"
              data-testid="tpg-import-preview"
            >
              <div><strong>{{ importPreview.answers }}</strong> คำตอบ · ปี {{ importPreview.yearId }} ·
                คอนฟิก v{{ importPreview.configVersionId }} · ชุดคำถาม v{{ importPreview.questionVersionId }}</div>
              <div class="text-caption mt-1">
                จาก session {{ importPreview.sessionId }} (ฟอร์ม #{{ importPreview.formId }})
                <span v-if="importPreview.exportedAt"> · {{ importPreview.exportedAt }}</span>
              </div>
              <div v-if="importPreview.hasPendingClarify" class="text-caption mt-1">
                มีคำถามที่ค้างอยู่ตอน export — จะกลับมาถามข้อนั้นต่อ
              </div>
            </v-alert>
            <v-alert
              v-if="importParseError"
              type="error"
              variant="tonal"
              density="comfortable"
              class="mt-3"
              data-testid="tpg-import-parse-error"
            >{{ importParseError }}</v-alert>

            <v-checkbox
              v-model="importClearTurns"
              density="compact"
              hide-details
              class="mt-2"
              :label="'ลบประวัติเทิร์นของ session นี้ด้วย (' + store.turns.length + ' เทิร์น)'"
              data-testid="tpg-import-clearturns"
            />
            <p class="text-caption text-medium-emphasis mt-1">
              แนะนำให้ลบ — ประวัติเดิมเป็นของฟอร์มคนละใบกับที่กำลังจะ import
            </p>
          </v-card-text>
          <v-card-actions class="pa-4">
            <v-spacer />
            <v-btn variant="outlined" data-testid="tpg-import-cancel" @click="importDialog = false">ยกเลิก</v-btn>
            <v-btn
              color="primary"
              variant="elevated"
              :loading="store.snapshotBusy"
              :disabled="!importPreview"
              data-testid="tpg-import-confirm"
              @click="confirmImport"
            >Import</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-snackbar v-model="snackbar" :color="snackbarColor" location="top" :timeout="snackbarTimeout">
        {{ snackbarMessage }}
        <template #actions>
          <v-btn variant="text" @click="snackbar = false">Close</v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </div>
</template>

<script>
// Tax Playground V2 console (S3-P4 · task 4-A) — host view.
//
// Three columns: turn timeline (P2) · chat (P0) · inspector (P0/P1 tabs:
// ฟอร์ม / Prompt & LLM / Tax JSON / Trace). Everything that survives a turn lives
// in `utils/taxPlaygroundV2Store.js`; this file owns display state only.
//
// The inspector is the WIDEST column from md up (`colSpans`), because it is the
// only one whose content is dense — the timeline and the chat routinely sit half
// empty. Two toggles let the admin push that further and both persist in prefs:
// collapse the timeline to a rail, or give the inspector the whole row.
//
// ─────────────────────────────────────────────────────────────────────────────
// SIX THINGS THAT LOOK LIKE STYLE BUT ARE LOAD-BEARING:
//
// 1. 🔴 `timelineDesc` reverses a COPY and CARRIES THE ORIGINAL ASC INDEX.
//    `store.turns` is ASC by `turnId` and `isRepeatTurn(i)` compares `turns[i-1]`.
//    Reversing and passing the NEW index shifts every "⟳ ถามซ้ำ" badge by one —
//    and the symptom only shows up when two tabs produce duplicate `turnNo`s.
//
// 2. 🔴 L6 ≠ L7. `/chat` answers HTTP 200 with `status: "Success"` even when the
//    LLM call failed (the node is fail-soft). A failed call is detected via
//    `statusReturn != 0` — the BINDING signal, sufficient on its own. `error` is
//    only a supplementary OR: it is legitimately `null` on a non-2xx provider
//    failure, so relying on it alone would under-fire → orange chip. Reading `status`
//    here would either never fire or paint every healthy turn red.
//
// 3. 🔴 L9 warns BEFORE the switch. The three version selectors use
//    `:model-value` + `@update:model-value`, never `v-model`: with `v-model` the
//    store is written the instant the user picks, so "ยกเลิก" would still abandon
//    the form on the next turn (silently). `cancelVersionSwitch()` touches
//    nothing, so the selector snaps back on its own.
//
// 4. 🔴 A 404 on a turn is an EMPTY STATE (L15). The store already turns it into
//    `turnNotFound` without throwing — the view must not add a red snackbar, and
//    it must strip `?turn=` so a refresh does not repeat the error.
//
// 5. 🔴 `counts` is never re-tallied anywhere (TC-63). The ONE number this view
//    computes is `askableCount`, for the ">80 refs" banner.
//
// 6. 🔴 Truncation is PER BLOB and needs TWO facts (TC-61): the guard named the
//    column in `trace`, AND that blob is missing from this payload. Either fact
//    alone lies — `trace` is echoed on the never-truncated inline `/chat` reply,
//    and a null DTO is also what an empty turn (formId = 0) legitimately looks
//    like. Each tab warns about the blob IT renders — `taxState` on Tax JSON,
//    never `variables`, which nothing on this screen shows.
// ─────────────────────────────────────────────────────────────────────────────
import { taxPlaygroundV2Store } from '../utils/taxPlaygroundV2Store';
import { splitTaxCards, parseTaxCardsField } from '../utils/taxCardMarker';
import { fmtThaiTime } from '../utils/thaiTime.js';
import TaxPlaygroundChatPanelV2 from '../components/TaxPlaygroundChatPanelV2.vue';
import TaxPlaygroundConfigFormPanelV2 from '../components/TaxPlaygroundConfigFormPanelV2.vue';
import TaxPlaygroundPromptViewerV2 from '../components/TaxPlaygroundPromptViewerV2.vue';
// Shared with `TaxLogV2Detail.vue` — it imports NOTHING (no store, no api client), which is
// what lets the same component render a production turn on a page that must stay clean of
// the playground's write path.
import TaxTurnFlowPanelV2 from '../components/TaxTurnFlowPanelV2.vue';

/** Above this size a JSON blob is shown compact — pretty-printing it freezes the tab (L14 ข). */
const JSON_BIG_BYTES = 200 * 1024;

/**
 * 🔴 The ONE per-blob truncation signal (TC-61).
 * `TaxPlaygroundV2BAL.GuardColumn()` / `RowSentinel()` append one Thai line per
 * cut column to `trace` before the row is written:
 *   • [playground] ตัดข้อมูล taxState: 812345 ไบต์ เกินเพดาน 262144 ไบต์ต่อคอลัมน์
 *   • [playground] ตัดข้อมูล variables: 412345 ไบต์ เพราะรวมทั้งแถวเกินเพดาน 1048576 ไบต์
 * The captured name is the DTO's JSON property name (`configForm`, `taxState`,
 * `llmCalls`, `variables`, …), so it maps 1:1 onto the fields this view renders.
 *
 * 🔴 D2 — the match is ANCHORED to the guard's own line prefix (`• [playground] `)
 * with the `m` flag, because `trace` also carries UNTRUSTED text: the node
 * processor echoes the user's typed answer (`• set {ref} → answered = {value}`)
 * and a 160-char LLM reply snippet into the same buffer. An unanchored match let
 * a crafted answer forge a phantom truncation banner. Both guard wordings must
 * keep matching — `… ไบต์ เกินเพดาน N ไบต์ต่อคอลัมน์` (GuardColumn) and
 * `… ไบต์ เพราะรวมทั้งแถวเกินเพดาน N ไบต์` (RowSentinel) — the tail after `ไบต์`
 * is captured in group 3 and only inspected for the `รวมทั้งแถว` marker.
 */
const TRUNCATION_TRACE_LINE = /^\s*•\s*\[playground\]\s+ตัดข้อมูล\s+([A-Za-z][A-Za-z0-9]*)\s*:\s*(\d+)\s*ไบต์([^\n]*)$/;

/**
 * T12 — Thai copy for `StateDto.kind` (`GET /state`). Display-only, so it lives
 * here and not in the store.
 * 🔴 An UNKNOWN kind falls back to the raw value, never to "คำถามปกติ": this is a
 * debugging console, and silently painting a kind the FE does not understand as
 * "business as usual" is exactly how a new backend state would go unnoticed.
 */
const STATE_KIND_TH = {
  START_CONFIRM: 'ทักทายเริ่มต้น',
  SELECT_YEAR: 'เลือกปีภาษี',
  RESUME: 'ทำต่อหรือเริ่มใหม่',
  COMPLETED: 'ฟอร์มปิดแล้ว (เมนูเริ่มแผนใหม่)',
  QUESTION: 'คำถามปกติ',
};

/**
 * The version-choice screen (a finished plan on a config/question pair the dropdown has moved past) comes back
 * as kind `RESUME` — as on the phone — and only `quickReplySource` = `versionChoice` / `versionChoiceMarker`
 * tells it apart from «ทำต่อ». See `lastStateKindTh`.
 */
const VERSION_CHOICE_KIND_TH = 'สร้างแผนใหม่ / ใช้ข้อมูลเดิม (แผนอยู่บนเวอร์ชันเก่า)';

export default {
  name: 'TaxPlaygroundV2',
  components: {
    TaxPlaygroundChatPanelV2,
    TaxPlaygroundConfigFormPanelV2,
    TaxPlaygroundPromptViewerV2,
    TaxTurnFlowPanelV2,
  },
  data() {
    return {
      flagNav: true,
      today: new Date().toLocaleDateString(),
      s: taxPlaygroundV2Store,
      store: taxPlaygroundV2Store.state,
      // display-only state (spec §Frontend line 1061 — deliberately NOT in the store)
      inspectorTab: null,
      settingsOpen: false,
      versionSwitchDialog: false,
      pendingVersionSelection: null,
      prevVersionSelection: null,
      resetDialog: false,
      clearTurnsChecked: false,
      importDialog: false,
      importText: '',
      importClearTurns: true,
      importParseError: null,
      elapsedSec: 0,
      elapsedTimer: null,
      pendingUserMessage: null,
      systemErrorText: null,
      taxJsonShowAll: false,
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      snackbarTimeout: 3500,
    };
  },
  computed: {
    // ── breakpoints ────────────────────────────────────────────────────
    display() {
      return (this.$vuetify && this.$vuetify.display) || {};
    },
    mdAndUp() {
      return Boolean(this.display.mdAndUp);
    },
    isXs() {
      return Boolean(this.display.xs);
    },
    smOnly() {
      return Boolean(this.display.smAndDown) && !this.isXs;
    },

    // ── control bar ────────────────────────────────────────────────────
    hasSession() {
      return Boolean(this.store.sessionId);
    },
    /** 🔴 `optionsLoaded`, not `!optionsLoading` — otherwise L3 flashes during bootstrap. */
    noYears() {
      return this.store.optionsLoaded && (this.store.options.years || []).length === 0;
    },
    /** L8 — validate, never normalize: `CIF001` stays `CIF001` and gets told off. */
    userIdError() {
      return this.s.isPgUserId(this.store.userId) ? '' : 'ต้องขึ้นต้นด้วย PG- (ห้ามใช้ CIF จริงของลูกค้า)';
    },
    canAct() {
      return !this.userIdError && !this.store.sending && !this.noYears;
    },
    canSend() {
      return this.canAct;
    },
    /**
     * The line the control bar leaves behind when it is collapsed.
     * 🔴 It names the four VERSIONS too, not just the session: collapsing must never
     * hide which config the turn about to be sent will actually run against — that is
     * the one fact this console exists to pin down.
     */
    settingsSummary() {
      const pick = (items, value) => {
        const hit = (items || []).find((i) => i && i.value === value);
        return hit ? hit.title : null;
      };
      const versions = [
        pick(this.yearItems, this.store.yearId),
        pick(this.configVersionItems, this.store.configVersionId),
        pick(this.questionVersionItems, this.store.questionVersionId),
        pick(this.settingsVersionItems, this.store.settingsVersionId),
      ].filter(Boolean);
      const parts = [this.store.userId, this.store.sessionId].filter(Boolean);
      if (versions.length) parts.push(versions.join(' / '));
      return parts.join(' · ');
    },
    yearItems() {
      return (this.store.options.years || []).map((y) => ({
        title: (y.taxYearCode || String(y.yearId)) + (y.isActive ? '' : ' (draft)'),
        value: y.yearId,
      }));
    },
    selectedYear() {
      return (this.store.options.years || []).find((y) => y.yearId === this.store.yearId) || null;
    },
    configVersionItems() {
      const y = this.selectedYear;
      return ((y && y.configVersions) || []).map((v) => ({
        title: (v.versionCode || String(v.versionId)) + (v.isActive ? '' : ' (draft)'),
        value: v.versionId,
      }));
    },
    selectedConfigVersion() {
      const y = this.selectedYear;
      return ((y && y.configVersions) || []).find((v) => v.versionId === this.store.configVersionId) || null;
    },
    questionVersionItems() {
      const cv = this.selectedConfigVersion;
      return ((cv && cv.questionVersions) || []).map((q) => ({
        title: (q.versionCode || String(q.questionVersionId)) + (q.isActive ? '' : ' (draft)'),
        value: q.questionVersionId,
      }));
    },
    /**
     * D5 — the settings-version picker that replaced the model picker.
     * `title` is the version code (the id is meaningless to an admin); `isActive` /
     * `description` ride along in `raw` for the chip + subtitle slots.
     */
    settingsVersionItems() {
      return (this.store.options.settingsVersions || []).map((v) => ({
        title: v.versionCode || `#${v.settingsVersionId}`,
        value: v.settingsVersionId,
        description: v.description || '',
        isActive: Boolean(v.isActive),
      }));
    },
    /**
     * Says WHY nothing is selectable when the settings table has not been applied yet
     * (`/options` returns an empty list, fail-soft) — an empty disabled dropdown with no
     * explanation reads as a bug. Otherwise it states the one thing that surprises people:
     * unlike the three version selectors, this one does NOT abandon the current form.
     */
    settingsVersionHint() {
      return this.settingsVersionItems.length
        ? 'มีผลกับเทิร์นถัดไป — ไม่ทิ้งฟอร์มปัจจุบัน'
        : 'ยังไม่มีเวอร์ชันตั้งค่าในระบบ — โหนดจะใช้ค่า default ในโค้ด';
    },

    // ── turn / inspector data ──────────────────────────────────────────
    activeTurn() {
      return this.store.activeTurn;
    },
    turnNotFound() {
      return Boolean(this.store.turnNotFound);
    },
    /**
     * `Get Tax State` must be able to fill the panel too (§Connection Points).
     * 🔴 D1 — but ONLY when no turn is open. `peekState()` sets `lastState`
     * WITHOUT clearing `activeTurn`, so falling back while viewing เทิร์น #N
     * rendered the LIVE form under the turn header and, worse, hid the
     * truncation banner (the blob "was there", just not this turn's).
     * Gate on `activeTurn` itself — same shape as `quickReply` / `quickReplySource`.
     */
    configForm() {
      if (this.activeTurn) return this.activeTurn.configForm || null;
      if (this.store.lastState && this.store.lastState.configForm) return this.store.lastState.configForm;
      return null;
    },
    // ── T12 · `Get Tax State` status bubble ────────────────────────────
    /**
     * 🔴 D1 — the peek slot, read RAW and read ALONE. It is deliberately NOT
     * merged with `activeTurn`: the card built on these getters is a sibling of
     * the Inspector, so a peek taken while เทิร์น #N is open adds a card and
     * changes nothing about that turn (header, ⟳ chip, truncation banner, form).
     */
    lastState() {
      return this.store.lastState;
    },
    /** Defaults to `QUESTION` the same way `TaxPlaygroundStateDto.Kind` does. */
    lastStateKind() {
      return (this.lastState && this.lastState.kind) || 'QUESTION';
    },
    lastStateKindTh() {
      // 🔴 RESUME is shared by «ทำต่อ» and the version-choice screen — the peek's OWN source decides (not
      // `quickReplySource`, which prefers the open turn). `off` (quick replies switched off in Settings) says
      // nothing about which screen it was, so that case keeps the plain RESUME label.
      const source = (this.lastState && this.lastState.quickReplySource) || '';
      if (this.lastStateKind === 'RESUME' && source.startsWith('versionChoice')) return VERSION_CHOICE_KIND_TH;
      return STATE_KIND_TH[this.lastStateKind] || this.lastStateKind;
    },
    /** `null` on `QUESTION` — the template renders NO box then (unchanged behaviour). */
    lastStateMessage() {
      return (this.lastState && this.lastState.message) || '';
    },
    /** 🔴 The PEEK's formId — not `formId`, which prefers the open turn. */
    lastStateFormId() {
      return (this.lastState && this.lastState.formId) || 0;
    },
    /**
     * The provenance the banner used to carry, moved onto the bubble itself (T12 / L16).
     *
     * 🔴 The first clause is the load-bearing one, not decoration: a chat bubble asserts "the flow said
     * this". A peek did NOT go through `/chat` — no LLM call, no turn, no write (`peekState`, D1) — and
     * without this line the console would be lying about what the flow actually produced.
     * 🔴 `kind` reaches the DOM through here and nowhere else on the page: it is a sibling of `taxState`,
     * not a member of it, so the Tax JSON tab never shows it, and Flow/Trace are empty on a peek by design.
     * An unrecognised kind falls through to its RAW value on purpose — that is how a new backend state
     * announces itself instead of shipping unnoticed.
     */
    peekMetaTh() {
      const parts = ['จาก Get Tax State · ยังไม่ได้ส่งข้อความ', this.lastStateKindTh];
      if (this.lastStateFormId) parts.push(`ฟอร์ม #${this.lastStateFormId}`);
      return parts.filter(Boolean).join(' · ');
    },

    /**
     * §7.3 #7 — the size guard cut `configForm` on its way into the row, so the
     * panel must say so instead of the "no form yet" empty state (L4).
     * 🔴 Proven by `trace` + a missing blob, NEVER by "the typed DTO is null".
     */
    configFormTruncated() {
      // 🔴 D1 — the predicate reads THIS TURN's blob, never a `Get Tax State` peek.
      return this.isBlobTruncated('configForm', this.activeTurn && this.activeTurn.configForm);
    },
    changedPaths() {
      return (this.activeTurn && this.activeTurn.changedPaths) || [];
    },
    /** The ONE number the FE is allowed to compute (§UX ruling D). */
    askableCount() {
      const groups = (this.configForm && this.configForm.groups) || [];
      return groups.reduce((n, g) => n + ((g && g.fields) || []).length, 0);
    },
    llmCalls() {
      return (this.activeTurn && this.activeTurn.llmCalls) || [];
    },
    /** What the customer typed this turn — the prompt viewer needs it to tell a turn that was answered
     *  without the extractor from one that had nothing to answer. */
    turnUserMessage() {
      return (this.activeTurn && this.activeTurn.userMessage) || null;
    },
    /** 🔴 L6 — the ONLY correct failure probe. */
    llmFailedCount() {
      return this.llmCalls.filter((c) => (c.statusReturn ?? 0) !== 0 || Boolean(c.error)).length;
    },
    askedFieldKey() {
      return (this.activeTurn && this.activeTurn.asked && this.activeTurn.asked.fieldKey) || null;
    },
    quickReply() {
      if (this.activeTurn) return this.activeTurn.quickReply || [];
      if (this.store.lastState) return this.store.lastState.quickReply || [];
      return [];
    },
    quickReplySource() {
      if (this.activeTurn) return this.activeTurn.quickReplySource || null;
      if (this.store.lastState) return this.store.lastState.quickReplySource || null;
      return null;
    },
    formStatus() {
      if (this.activeTurn && this.activeTurn.progress) return this.activeTurn.progress.formStatus ?? null;
      if (this.store.lastState && this.store.lastState.progress) return this.store.lastState.progress.formStatus ?? null;
      return null;
    },
    formId() {
      if (this.activeTurn && this.activeTurn.formId) return this.activeTurn.formId;
      if (this.store.lastState && this.store.lastState.formId) return this.store.lastState.formId;
      return 0;
    },
    /**
     * The parsed snapshot, or null while the textarea does not hold a usable one.
     * Doubles as the Import button's enable test — parsing here rather than on submit
     * means a bad paste is caught before anything in the session is touched.
     * Sets `importParseError` as a side effect so the reason is visible.
     */
    importPreview() {
      const raw = (this.importText || '').trim();
      if (!raw) { this.importParseError = null; return null; }
      let snap;
      try {
        snap = JSON.parse(raw);
      } catch (e) {
        this.importParseError = 'ไฟล์นี้ไม่ใช่ JSON ที่อ่านได้';
        return null;
      }
      if (!snap || snap.kind !== 'tax-planning-v2-form-snapshot') {
        this.importParseError = 'ไฟล์นี้ไม่ใช่ snapshot ของ Tax Planning V2';
        return null;
      }
      const form = snap.form || {};
      if (!(Number(form.questionVersionId) > 0)) {
        this.importParseError = 'snapshot ไม่มีเวอร์ชันชุดคำถาม (questionVersionId)';
        return null;
      }
      this.importParseError = null;
      return {
        snapshot: snap,
        answers: (snap.answers || []).length,
        yearId: form.yearId,
        configVersionId: form.taxConfigVersionId,
        questionVersionId: form.questionVersionId,
        hasPendingClarify: Boolean(form.pendingClarifyJson) || Number(form.pendingQuestionItemId) > 0,
        sessionId: (snap.source && snap.source.sessionId) || '—',
        formId: (snap.source && snap.source.formId) || 0,
        exportedAt: snap.exportedAt || null,
      };
    },
    /** D2 — only `/chat` sets this meaningfully; `/state` always echoes null. */
    versionAbandonedFormId() {
      const v = (this.activeTurn && this.activeTurn.versions) || null;
      return (v && v.versionSwitchAbandonedFormId) || null;
    },
    /**
     * 🔴 Per-blob truncation map — `{ [blobName]: { bytes, scope } }` (TC-61).
     *
     * TWO signals feed it:
     *  1. `trace` names every column the BAL size guard cut. It is the ONLY
     *     per-column evidence that survives: the typed DTOs (`configForm`,
     *     `taxState`, `asked`, `quickReply`, `llmCalls`, `changedPaths`) come
     *     back stripped to null / [] and CANNOT carry the sentinel.
     *  2. `variables` is a `Dictionary<string,object>`, so it round-trips the
     *     `{__truncated,reason,bytes}` sentinel intact — kept as a cross-check.
     *
     * 🔴 NEVER infer a cut from "the typed DTO came back null". null is equally
     * the honest answer on a legitimately empty turn (formId = 0, an error turn,
     * the first turn) — that guess is the false warning this map replaces.
     */
    truncatedBlobs() {
      const out = {};
      const trace = (this.activeTurn && this.activeTurn.trace) || '';
      if (trace) {
        // Fresh regex per run — a shared /g literal carries `lastIndex` across calls.
        const re = new RegExp(TRUNCATION_TRACE_LINE.source, 'gm');
        let m = re.exec(trace);
        while (m) {
          const hit = {
            bytes: Number(m[2]) || 0,
            scope: (m[3] || '').indexOf('รวมทั้งแถว') >= 0 ? 'row' : 'column',
          };
          // 🔴 D8 — a blob can be cut TWICE (column cap, then the row cap re-cuts the
          // ~90-byte sentinel). Last-write-wins made the note read `ต้นฉบับ ~92 ไบต์`.
          // Keep the LARGEST size seen — that is the original blob.
          const prev = out[m[1]];
          if (!prev || hit.bytes > prev.bytes) out[m[1]] = hit;
          m = re.exec(trace);
        }
      }
      const vars = this.activeTurn && this.activeTurn.variables;
      if (this.s.isTruncated(vars) && !out.variables) {
        out.variables = {
          bytes: Number(vars.bytes) || 0,
          scope: /row exceeded/i.test(String(vars.reason || '')) ? 'row' : 'column',
        };
      }
      return out;
    },
    /** The Tax JSON tab renders `taxState` — so it warns about `taxState`, never about `variables`. */
    taxStateTruncated() {
      // 🔴 D1 — same rule as `configFormTruncated`: this turn's blob, not `lastState`.
      return this.isBlobTruncated('taxState', this.activeTurn && this.activeTurn.taxState);
    },
    /** Same failure mode one tab over: a cut `llmCallsJson` deserializes to an empty list. */
    llmCallsTruncated() {
      return this.isBlobTruncated('llmCalls', this.llmCalls.length ? this.llmCalls : null);
    },
    configFormTruncatedNote() {
      return this.truncationNote('configForm');
    },
    taxStateTruncatedNote() {
      return this.truncationNote('taxState');
    },
    llmCallsTruncatedNote() {
      return this.truncationNote('llmCalls');
    },
    /** 🔴 D1 — `lastState` may only fill the JSON tab when NO turn is open (see `configForm`). */
    taxStateObject() {
      if (this.activeTurn) return this.activeTurn.taxState || null;
      if (this.store.lastState && this.store.lastState.taxState) return this.store.lastState.taxState;
      return null;
    },
    taxJsonCompact() {
      return this.taxStateObject ? JSON.stringify(this.taxStateObject) : '';
    },
    taxJsonKb() {
      return Math.round(this.taxJsonCompact.length / 1024);
    },
    taxJsonTooBig() {
      return this.taxJsonCompact.length > JSON_BIG_BYTES && !this.taxJsonShowAll;
    },
    taxStateText() {
      if (!this.taxStateObject) {
        // "ไม่มีข้อมูล" and "ข้อมูลหาย" are different facts — the pre must not claim the first for the second.
        return this.taxStateTruncated
          ? 'ข้อมูลก้อนนี้ถูกตัดตอนบันทึก จึงไม่มี JSON ให้แสดง'
          : 'ยังไม่มีข้อมูลฟอร์มของเทิร์นนี้';
      }
      if (this.taxJsonTooBig) return this.taxJsonCompact;
      return JSON.stringify(this.taxStateObject, null, 2);
    },
    traceText() {
      return (this.activeTurn && this.activeTurn.trace) || 'ไม่มี trace ของเทิร์นนี้';
    },

    // ── flow tab ───────────────────────────────────────────────────────
    /**
     * The structured "life of one turn" (`TaxTurnFlowV2Dto`) of the SELECTED turn.
     *
     * 🔴 D1 — and here the gate is not a preference, it is the only honest answer:
     * `peekState()` writes `store.lastState` WITHOUT clearing `activeTurn`, and
     * `lastState` carries no flow at all. Falling back to it the way `configForm`
     * does would paint the CURRENT form's steps over เทิร์น #N — so a peek simply
     * leaves this tab empty.
     * 🔴 `null` here is honestly ambiguous (turn recorded before this tab existed /
     * turn did nothing / the row's size guard shed the blob). The panel — not this
     * view — decides which of the three to say, which is why it also gets `traceText`.
     */
    turnFlow() {
      return (this.activeTurn && this.activeTurn.flow) || null;
    },
    /**
     * 🔴 NOT `turnFlow != null`. "ยังไม่ได้เลือกเทิร์น" and "เลือกแล้ว แต่เทิร์นนั้น
     * ไม่มี flow" are different facts with different copy in the panel; collapsing
     * them tells a tester who has opened nothing that the data is missing.
     */
    turnSelected() {
      return Boolean(this.activeTurn);
    },
    /**
     * Steps where the turn threw something away — the same badge rule as `llm`
     * (🔴 L6: never `status`, which is always "Success" because the node fails soft).
     * `skipped`/`noop` are deliberately NOT counted: they are the normal shape of a
     * healthy turn, and a badge that is always on says nothing.
     */
    flowProblemCount() {
      const steps = (this.turnFlow && Array.isArray(this.turnFlow.steps) && this.turnFlow.steps) || [];
      return steps.filter((s) => s && (s.outcome === 'rejected' || s.outcome === 'error')).length;
    },

    // ── timeline ───────────────────────────────────────────────────────
    /** 🔴 Newest first for the eye, ORIGINAL ASC index for `isRepeatTurn`. */
    timelineDesc() {
      return this.store.turns.map((t, i) => ({ t, i })).reverse();
    },
    repeatStreak() {
      return this.s.repeatStreak();
    },
    activeTurnIsRepeat() {
      if (!this.activeTurn) return false;
      const idx = this.store.turns.findIndex((t) => t && t.turnId === this.activeTurn.turnId);
      return idx > 0 ? this.s.isRepeatTurn(idx) : false;
    },
    activeRepeatTitle() {
      return this.repeatTitle({ askedFieldKey: this.askedFieldKey });
    },

    // ── chat bubbles ───────────────────────────────────────────────────
    /**
     * `/turns` truncates `replyPreview` at 160 chars and `userMessage` at 200, so
     * the turn currently open reads its FULL text from `activeTurn` instead.
     * Bot bubbles additionally carry `cardText` / `cards` / `cardsDiffer` — the
     * `|taxcard|` payloads for the panel to render below the bubble. See the
     * comment inside the push for why `text` stays raw.
     * 🔴 การ์ดขึ้น **ทุกเทิร์น** ไม่ใช่เฉพาะเทิร์นที่เลือกอยู่: เทิร์น active แยกการ์ดจาก `reply` เต็มเอง
     * ส่วนเทิร์นอื่นอ่านจากคอลัมน์ `taxCards` ของแถวสรุป — เหตุผลเต็มอยู่เหนือ `const split` ข้างล่าง
     */
    bubbles() {
      const out = [];
      const active = this.activeTurn;
      this.store.turns.forEach((t) => {
        if (!t) return;
        const isActive = Boolean(active && active.turnId === t.turnId);
        const userMessage = isActive ? (active.userMessage || t.userMessage) : t.userMessage;
        if (userMessage) {
          out.push({
            key: `u-${t.turnId}`,
            side: 'user',
            text: userMessage,
            time: t.createdDate,
            turnId: t.turnId,
            llmFailed: false,
          });
        }
        const reply = isActive ? active.reply : t.replyPreview;
        // 🔴 เทิร์น ACTIVE กับเทิร์นที่ไม่ active เดินคนละทางโดยตั้งใจ — ไม่ใช่โค้ดซ้ำซ้อนที่รอรวบ:
        //   · ACTIVE — มี `reply` เต็ม (ไม่ถูกตัด และ marker ยังอยู่ในสาย) ⇒ แยกการ์ดด้วย `splitTaxCards` เอง
        //     เพราะหน้าจอนี้มีไว้ "ตรวจ wire format ด้วยตา": payload ที่ parse ไม่ผ่านต้องโผล่มาเป็นซากให้เห็น
        //     (`doc: null`) และ `cardsDiffer` ต้องเทียบผลแยกของ FE กับ `taxCards` ที่ backend แยกจากข้อความเดียวกันได้
        //   · ไม่ ACTIVE — `GET /turns` คืนแต่ `replyPreview` (ตัดที่ 160 ตัวอักษร และตัด marker ทิ้งไปแล้ว)
        //     จึงไม่มีสายดิบให้ split เลย ⇒ อ่านการ์ดจากคอลัมน์ `taxCards` ของแถวแทน (ฟิลด์เดียวกับของ turn เต็ม
        //     ทุกประการ: JSON array string ที่ `TaxCardMarkerV2.SplitMarker` ยกออกมาจาก reply) การ์ดจึงขึ้นครบ
        //     ทุกเทิร์นตลอดเวลา ไม่ต้องคลิกเลือกเทิร์นก่อน
        // 🔴 `parseTaxCardsField` คืน **doc ล้วน ๆ** ไม่ใช่ `{ raw, doc }` ที่ panel/`TaxSummaryCardV2` ต้องการ
        //    จึงต้อง map เอง และ `raw` ถูก re-serialise (เหมือนที่ `splitTaxCards` ทำกับ legacy array) เพราะแถวสรุป
        //    ไม่มี source span ของ payload ให้เก็บไว้ — string นี้ถูกใช้แค่ในตัวอ่าน JSON เท่านั้น
        // 🔴 backend รุ่นก่อนที่ยังไม่ส่ง `taxCards` มากับแถว ⇒ `t.taxCards` เป็น undefined ⇒ `parseTaxCardsField`
        //    คืน `[]` (guard `json == null` ตัวแรกของมัน) ⇒ ได้พฤติกรรมเดิมเป๊ะ ไม่ throw
        const split = isActive ? splitTaxCards(reply) : null;
        const cards = split
          ? split.cards
          : parseTaxCardsField(t.taxCards).map((doc) => ({ raw: JSON.stringify(doc), doc }));
        // 🔴 `beCards` (และ `cardsDiffer` ที่ใช้มัน) คิดเฉพาะตอน active เท่านั้น — เหมือนเดิม: เทิร์นที่ไม่ active
        //    ไม่มี reply เต็มให้ split จึงไม่มี "ความเห็นที่สอง" ให้เทียบ ทั้งสองฝั่งกลายเป็นแหล่งเดียวกัน
        //    (คอลัมน์ `taxCards`) — ถ้าเผลอเทียบจะได้ 0-vs-N ติดธงส้มทุกแถวโดยไม่มีอะไรผิดจริง
        const beCards = isActive ? parseTaxCardsField(active.taxCards) : [];
        // A card whose JSON did not parse (`doc: null`) — kept on purpose by `splitTaxCards` as evidence.
        const anyBroken = cards.some((c) => !c.doc);
        // 🔴 เงื่อนไขคือ "มีข้อความ **หรือ** มีการ์ด" ไม่ใช่ `if (reply)` เฉย ๆ เพราะ CARD-ONLY turn (lead text ว่าง
        // + `|taxcard|` — รูปที่ node รองรับโดยตั้งใจ เพราะการ์ดส่วนใหญ่มีหัวข้อของตัวเองอยู่แล้ว) ได้
        // `replyPreview = ''` เสมอ: backend เขียน preview จาก `replyClean` คือหลังตัด marker ออกแล้ว
        // เดิมจึงไม่ได้ bot bubble เลยเมื่อเทิร์นนั้นไม่ใช่เทิร์นที่เลือกอยู่ และ comment ตรงนี้เคยระบุว่าต้องรอ
        // backend ใส่ placeholder ให้ `replyPreview` — 🔴 ไม่จริงอีกต่อไปแล้ว เมื่อ `taxCards` ติดมากับแถว การ์ดก็
        // มาถึงฝั่งนี้ตรง ๆ โดยไม่ต้องพึ่งข้อความนำ ส่วน bubble เทา ๆ ที่ว่างเปล่าถูกกันไว้ที่ panel อยู่แล้ว
        // (TaxPlaygroundChatPanelV2.vue — `v-if="bubbleText(b).trim() || !(b.cards && b.cards.length)"`)
        if (reply || cards.length) {
          out.push({
            key: `b-${t.turnId}`,
            side: 'bot',
            // 🔴 `text` stays RAW — |taxcard| marker and all. This screen is where the wire format gets
            // checked before the app is asked to parse it, so the raw string must survive untouched.
            text: reply,
            // The stripped copy rides ALONGSIDE it: the panel renders cards BELOW the bubble (an addition,
            // never a replacement) and its "ดู marker ดิบ" switch flips the bubble back to `text`, so the
            // by-eye format check this screen exists for is still one click away.
            // 🔴 `null` เมื่อไม่ใช่เทิร์น active (`split` เป็น null) — ไม่มี reply เต็มให้ถอด marker จึงไม่มีสำเนา
            //    ที่ถอดแล้ว ปล่อยให้ `bubbleText` ตกไปใช้ `text` = `replyPreview` ซึ่ง backend ตัด marker ให้แล้ว
            cardText: split && cards.length ? split.text : null,
            cards,
            // 🔴 FE-split count vs the array the backend lifted out of the same reply. A mismatch means the
            // two parsers disagree about the wire — exactly the format drift this console is here to catch.
            // 🔴 Compared ONLY while every payload parsed. The two sides count a BROKEN card differently by
            // design: `splitTaxCards` keeps it as `{ raw, doc: null }` so the debris stays on screen, while
            // the backend hands over `"[" + join(",", docs) + "]"`, which `parseTaxCardsField` answers `[]`
            // for the moment that string is not valid JSON. So a template that emits a malformed payload
            // would ALWAYS read as 1-vs-0 and accuse the marker format — while the same row already carries
            // the red "อ่าน payload ของการ์ดใบนี้ไม่ได้" alert naming the real cause. One accurate signal
            // beats a second one pointing the reader at the wrong file.
            cardsDiffer: isActive && !anyBroken && cards.length !== beCards.length,
            time: t.createdDate,
            turnId: t.turnId,
            llmFailed: isActive ? this.llmFailedCount > 0 : false,
          });
        }
      });
      // ── the "Get Tax State" peek, as an ordinary chat message ───────────────────────────────────
      // It used to be a tonal card above the chat. Same data, same lifetime — but read where a tester
      // actually reads: in the conversation, in the order it belongs.
      //
      // 🔴 ABOVE `pending-user` on purpose. `send()` clears `lastState` only AFTER the POST resolves, and
      //    a CALCULATE turn runs for minutes — appended last, the peek would render as a bot message that
      //    arrived after the tester's own message.
      // 🔴 Three guards, each of which is a bug without it:
      //    · a blank message — `kind === 'QUESTION'` carries `message: null`, and a card-less bubble with
      //      empty text passes the panel's empty-bubble guard and paints a blank grey pill;
      //    · `!activeTurn` — a peek describes the LIVE form. Hung under เทิร์น #12 it reads as that turn's
      //      reply. This is the SAME condition `quickReply()` uses to fall back to `lastState`, so the
      //      bubble and the chips underneath it can never disagree about which state they belong to;
      //    · not a repeat of the newest bot bubble — after a real turn the reply already says it.
      // 🔴 `side: 'state'`, never `'bot'` — the panel's `liveBubbleKey` takes the last `'bot'` bubble to
      //    decide which card is still interactive. A 'bot' peek would silently freeze the newest card.
      const peekMsg = this.lastStateMessage;
      if (peekMsg && !this.activeTurn) {
        const lastBot = out.filter((b) => b.side === 'bot').pop();
        if (!lastBot || String(lastBot.text || '').trim() !== peekMsg.trim()) {
          out.push({
            key: 'peek-state',
            side: 'state',
            // RAW, exactly as the banner showed it: `|quickReply|` is stripped server-side and the state
            // endpoint never emits `|taxcard|`, so there is nothing to split and no `cards` key to add.
            text: peekMsg,
            meta: this.peekMetaTh,
            time: null,
            turnId: 0,   // click-inert — the panel only emits `select-turn` for a real turn id
            llmFailed: false,
          });
        }
      }

      // L5 (ก) — optimistic echo of what the admin just typed.
      if (this.pendingUserMessage) {
        out.push({
          key: 'pending-user',
          side: 'user',
          text: this.pendingUserMessage,
          time: null,
          turnId: 0,
          llmFailed: false,
        });
      }
      // L7 — a real playground failure, as a red system bubble (never a dead page).
      if (this.systemErrorText) {
        out.push({
          key: 'system-error',
          side: 'system',
          text: `ทดสอบไม่สำเร็จ: ${this.systemErrorText}`,
          time: null,
          turnId: 0,
          llmFailed: false,
        });
      }
      return out;
    },

    // ── tabs ───────────────────────────────────────────────────────────
    /**
     * `ฟอร์ม` is the FIRST tab (and therefore the default) on every breakpoint —
     * 5 tabs at sm/md+, 6 at xs where `แชท` is prepended because the whole page
     * collapses into this one strip there.
     * 🔴 Desktop used to get only 3 tabs, with the Form Template pinned above them.
     * Halving the column made both halves unreadable, so the pin is gone.
     * `Flow` sits next to `Trace` because they are the two readings of the SAME
     * recorder — one structured, one flat — and its label is one short word so the
     * strip does not grow wider in the narrow inspector column (`show-arrows`
     * handles the overflow that is left).
     */
    tabDefs() {
      const llm = {
        value: 'llm',
        label: `Prompt & LLM (${this.llmCalls.length})`,
        color: this.llmFailedCount > 0 ? 'orange' : undefined,
        icon: this.llmFailedCount > 0 ? 'mdi-alert' : null,
      };
      /**
       * Same badge rule as `llm`, for the same reason: someone opens this tab because
       * something went wrong, so a turn that rejected an update has to be visible
       * BEFORE the tab is opened. Icon only — the label stays `Flow`.
       */
      const flow = {
        value: 'flow',
        label: 'Flow',
        color: this.flowProblemCount > 0 ? 'orange' : undefined,
        icon: this.flowProblemCount > 0 ? 'mdi-alert' : null,
      };
      const withForm = [
        { value: 'form', label: 'ฟอร์ม' },
        llm,
        { value: 'json', label: 'Tax JSON' },
        flow,
        { value: 'trace', label: 'Trace' },
      ];
      return this.isXs ? [{ value: 'chat', label: 'แชท' }].concat(withForm) : withForm;
    },
    /**
     * Falls back to the first tab of the CURRENT breakpoint, so resizing can never
     * strand it. `inspectorTab` starts as `null` and is NOT persisted (the store's
     * prefs blob holds the 6 selectors + `timelineCollapsed`/`inspectorFull` only),
     * so a fresh load always lands on `form`, while a tab the admin picked in this
     * session survives every re-render — including `@view-tax-json`.
     */
    activeTab() {
      const defs = this.tabDefs;
      const hit = defs.find((t) => t.value === this.inspectorTab);
      return hit ? hit.value : (defs[0] && defs[0].value);
    },
    /**
     * 🔴 The `-sm` twins exist for ONE breakpoint only. The form tab body is shared
     * markup now (xs branch + tablet/desktop branch), so the suffix is decided here
     * instead of being baked into three copies: `sm` keeps `tpg-cf-*-sm`, and BOTH
     * `xs` and `md+` keep the unsuffixed `tpg-cf-*` the existing cases look for.
     */
    cfTruncatedTestId() {
      return this.smOnly ? 'tpg-cf-truncated-sm' : 'tpg-cf-truncated';
    },
    cfEmptyTestId() {
      return this.smOnly ? 'tpg-cf-empty-sm' : 'tpg-cf-empty';
    },

    // ── horizontal layout (persisted in prefs) ─────────────────────────
    /** Column A shrinks to a rail. Only meaningful at md+ — below that A is not rendered at all. */
    timelineCollapsed() {
      return Boolean(this.store.timelineCollapsed);
    },
    /** Columns A+B are dropped and the inspector owns the row. */
    inspectorFull() {
      return Boolean(this.store.inspectorFull);
    },
    /**
     * Grid spans per layout mode. Two rules hold in every mode:
     *  · the inspector is the widest column from md up (≥ 5/12 at md, ≥ 6/12 at lg) —
     *    it is the column that actually carries dense data;
     *  · `sm` stays a 6/6 two-column split and `xs` never reaches this markup at all
     *    (that breakpoint renders the separate tabbed block above), so no mode here
     *    can break the single-column layout.
     * 🔴 When the timeline is a rail its span is `auto` (≈44 px), so chat+inspector
     * are given 11/12 — NOT 12/12, which would wrap the rail onto its own line.
     * 1/12 is ≥ 80 px at every breakpoint this branch renders on, so the rail
     * always fits in the twelfth that was left for it. `lg` cascades to xl/xxl.
     */
    colSpans() {
      const chat = { cols: 12, sm: 6, md: 5, lg: 4 };
      if (this.inspectorFull) {
        return {
          timeline: { cols: 'auto', md: 'auto', lg: 'auto' },
          chat,
          inspector: { cols: 12, sm: 12, md: 12, lg: 12 },
        };
      }
      if (this.timelineCollapsed) {
        return {
          timeline: { cols: 'auto', md: 'auto', lg: 'auto' },
          chat,
          inspector: { cols: 12, sm: 6, md: 6, lg: 7 },
        };
      }
      return {
        timeline: { cols: 12, md: 2, lg: 2 },
        chat,
        inspector: { cols: 12, sm: 6, md: 5, lg: 6 },
      };
    },
  },
  /**
   * 🔴 `created`, not `mounted` — the control bar must not render open for one frame
   * and then snap shut on xs. Same default as before the toggle went cross-breakpoint:
   * collapsed on xs, open from sm up.
   */
  created() {
    this.settingsOpen = !this.isXs;
  },
  async mounted() {
    try {
      await this.s.loadOptions();
    } catch (err) {
      this.showError((err && err.message) || 'โหลดตัวเลือกไม่สำเร็จ');
    }
    const query = this.$route.query || {};
    await this.s.applyDeepLink({ session: query.session, turn: query.turn });
    // 🔴 TC-62 — strip a dead `?turn=` so a refresh does not repeat the miss.
    if (this.store.turnNotFound) {
      const next = Object.assign({}, query);
      delete next.turn;
      this.$router.replace({ query: next });
    }
    // 🔴 LAST, and both positions matter: AFTER `loadOptions()` so the settings version and the version triple
    // are settled (a peek on the wrong version previews the wrong greeting copy / version-choice screen), and AFTER `applyDeepLink` because a `?turn=`
    // opens a turn — which `autoPeek` then honours by doing nothing.
    await this.autoPeek();
  },
  beforeUnmount() {
    this.stopElapsed();
  },
  methods: {
    // ── layout (persisted) ────────────────────────────────────────────
    /**
     * 🔴 Store setters, never `v-model` on `store.*` — same rule as the version
     * selectors (see note 3 in the header): the view decides when a change is
     * committed, and every commit goes through `savePrefs()` exactly once.
     */
    setTimelineCollapsed(value) {
      this.s.setTimelineCollapsed(value);
    },
    /**
     * Leaving full width restores whatever the timeline was doing before — the
     * two prefs are independent, so nothing needs to be remembered separately.
     */
    toggleInspectorFull() {
      this.s.setInspectorFull(!this.inspectorFull);
    },

    // ── L14 · truncation (TC-61) ──────────────────────────────────────
    /**
     * 🔴 A blob is truncated when the guard RECORDED the cut in `trace` **and**
     * the data really is not here. Both halves are load-bearing:
     *  · `trace` alone lies on the inline `POST /chat` reply — that payload is
     *    never truncated, yet its trace already carries the guard's notes
     *    (`TaxPlaygroundV2BAL.cs:458`), so the very same turn holds FULL data.
     *  · "the DTO is null" alone lies on every turn that never had the blob
     *    (formId = 0, error turn, first turn) — the original TC-61 defect.
     * @param {string} name  JSON property name as the BAL spells it in `trace`
     * @param {*} value      what this view would actually render for that blob
     */
    isBlobTruncated(name, value) {
      return Boolean(this.truncatedBlobs[name]) && !value;
    },
    /** Thai one-liner naming the cap that fired and the size that tripped it. */
    truncationNote(name) {
      const hit = this.truncatedBlobs[name];
      if (!hit) return '';
      const cap = hit.scope === 'row' ? 'ทั้งแถวเกิน 1 MB' : 'เกิน 256 KB ต่อคอลัมน์';
      const size = hit.bytes ? ` · ต้นฉบับ ${hit.bytes.toLocaleString()} ไบต์` : '';
      return `ข้อมูลก้อนนี้ถูกตัดตอนบันทึก (${cap}${size}) — ดูรายละเอียดในแท็บ Trace`;
    },

    // ── session / control bar ─────────────────────────────────────────
    async onNewSession() {
      this.s.newSession();
      this.systemErrorText = null;
      this.$router.replace({ query: { session: this.store.sessionId } });
      await this.autoPeek();
    },
    async onGetState() {
      try {
        await this.s.peekState();
      } catch (err) {
        this.showError((err && err.message) || 'ดึงสถานะฟอร์มไม่สำเร็จ');
      }
    },
    /**
     * The peek the tester used to have to press for, at the two moments a session APPEARS: page load and
     * "สุ่ม session ใหม่".
     *
     * 🔴 NOT a watcher. The SessionId and CIF fields commit to the store on EVERY KEYSTROKE (no `.lazy`,
     * no debounce anywhere in this view), so anything reactive means one `GET /state` per character with
     * the responses racing each other into `lastState` — the last one to land can be a half-typed prefix's.
     * Two explicit call sites instead.
     * 🔴 SILENT. `peekState()` re-throws, and `onGetState` turns that into a red snackbar — correct for a
     * button a human pressed, wrong for a call nobody made. A backend hiccup must not make "สุ่ม session
     * ใหม่" look like a failure. Same swallow the snapshot import already uses.
     * 🔴 `canAct` first: it is what the Get Tax State button itself is gated on, and it is what keeps a
     * half-typed or non-`PG-` userId from reaching the endpoint (which answers 400) during bootstrap.
     * 🔴 `!activeTurn`: a peek describes the live form, and with a turn open the bubble would hang under
     * it. Same gate `bubbles()` and `quickReply()` use.
     * 🔴 …and NOT on a session that already has turns — see the guard below.
     */
    async autoPeek() {
      if (!this.canAct || this.activeTurn || this.store.peeking) return;
      // 🔴 A session that ALREADY HAS HISTORY is left alone. The peek answers "where does this form stand
      //    right now?", which is the question a tester has on a session with nothing in it yet — on one with
      //    a transcript the newest bot bubble already answers it, and the peek would hang a second copy of
      //    the same question under it. Pressing Get Tax State by hand still works and is the way to ask on
      //    purpose. `turnsTotal` AND the loaded page: the count is authoritative, but it is 0 when the
      //    `/turns` call failed, in which case the rows already on screen are the honest signal.
      if (this.store.turnsTotal > 0 || (this.store.turns || []).length > 0) return;
      try {
        await this.s.peekState();
      } catch (e) {
        // swallowed on purpose — see the note above
      }
    },
    async onRefreshTurns() {
      try {
        await this.s.loadTurns();
      } catch (err) {
        this.showError((err && err.message) || 'โหลดไทม์ไลน์ไม่สำเร็จ');
      }
    },
    openTaxConfig() {
      window.open('/TaxConfigurationV2', '_blank');
    },

    // ── L9 · version switch ───────────────────────────────────────────
    /** Warn while `formStatus` is null too — over-warning beats losing a form silently (§7.3 #2). */
    shouldWarnVersionSwitch() {
      return this.store.turns.length > 0 && (this.formStatus === null || this.formStatus === 'InProgress');
    },
    /**
     * Start from the current triple, replace the changed leg, then drop any
     * downstream id that no longer belongs to the new parent — an inconsistent
     * triple would pin a form to versions from a different year.
     */
    mergeSelection(kind, value) {
      const next = {
        yearId: this.store.yearId,
        configVersionId: this.store.configVersionId,
        questionVersionId: this.store.questionVersionId,
      };
      next[kind] = value;
      const year = (this.store.options.years || []).find((y) => y.yearId === next.yearId) || null;
      const configVersions = (year && year.configVersions) || [];
      const cv = configVersions.find((v) => v.versionId === next.configVersionId) || null;
      if (!cv) next.configVersionId = null;
      const questionVersions = (cv && cv.questionVersions) || [];
      if (!questionVersions.some((q) => q.questionVersionId === next.questionVersionId)) {
        next.questionVersionId = null;
      }
      return next;
    },
    /** 🔴 Nothing is written to the store until the admin confirms (TC-58). */
    onVersionChange(kind, value) {
      const next = this.mergeSelection(kind, value);
      if (this.shouldWarnVersionSwitch()) {
        this.prevVersionSelection = {
          yearId: this.store.yearId,
          configVersionId: this.store.configVersionId,
          questionVersionId: this.store.questionVersionId,
        };
        this.pendingVersionSelection = next;
        this.versionSwitchDialog = true;
        return;
      }
      this.s.setVersionSelection(next);
    },
    confirmVersionSwitch() {
      if (this.pendingVersionSelection) this.s.setVersionSelection(this.pendingVersionSelection);
      this.versionSwitchDialog = false;
      this.pendingVersionSelection = null;
      this.prevVersionSelection = null;
    },
    /** Touches nothing: the store was never written, so the selector reverts by itself. */
    cancelVersionSwitch() {
      this.versionSwitchDialog = false;
      this.pendingVersionSelection = null;
      this.prevVersionSelection = null;
    },
    /**
     * 🔴 DELIBERATELY NOT `onVersionChange` — no L9 confirm here.
     * The L9 dialog exists because the year/config/question triple is part of the FORM's
     * identity: switching it makes the backend abandon the in-progress form and restart at
     * question 1. The settings version is not — the abandon guard is wired to the explicit
     * triple only, and the settings version merely decides which LLM config + prompt copy the
     * NEXT turn is built from. Warning "จะทิ้งฟอร์มเดิม" here would be a lie, and blocking a
     * harmless switch mid-form removes the whole point of a debugging console (compare two
     * prompt versions against the SAME half-answered form). The `:model-value` +
     * `@update:model-value` pair is still kept — never `v-model` on the store (see note 3).
     */
    onSettingsVersionChange(value) {
      this.s.setSettingsVersionId(value);
    },

    // ── L10 · reset ───────────────────────────────────────────────────
    openResetDialog() {
      this.clearTurnsChecked = false;
      this.resetDialog = true;
    },
    async confirmReset() {
      try {
        const res = await this.s.reset({ clearTurns: this.clearTurnsChecked });
        this.systemErrorText = null;
        const abandoned = (res && res.abandonedFormId) || 0;
        this.showSnack(abandoned ? `ทิ้งฟอร์ม #${abandoned} แล้ว — เริ่มถามใหม่ได้เลย` : 'ไม่มีฟอร์มที่ต้องทิ้ง — เริ่มถามใหม่ได้เลย');
        this.resetDialog = false;
      } catch (err) {
        this.showError((err && err.message) || 'รีเซ็ตไม่สำเร็จ');
      }
    },

    // ── snapshot export / import ──────────────────────────────────────
    async onExportForm() {
      try {
        const snap = await this.s.exportForm();
        const json = JSON.stringify(snap, null, 2);
        const stamp = (snap && snap.source && snap.source.sessionId) || this.store.sessionId || 'session';
        const url = URL.createObjectURL(new Blob([json], { type: 'application/json' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `taxform-${stamp}-f${(snap && snap.source && snap.source.formId) || 0}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        // Revoked on the next tick, not immediately: Safari cancels an in-flight
        // download when the object URL disappears in the same task.
        setTimeout(() => URL.revokeObjectURL(url), 0);
        this.showSnack(`บันทึก snapshot แล้ว — ${(snap.answers || []).length} คำตอบ`);
      } catch (err) {
        if (err && err.httpStatus === 404) {
          this.showError('session นี้ยังไม่มีฟอร์ม — คุยอย่างน้อย 1 เทิร์นก่อน');
          return;
        }
        this.showError((err && err.message) || 'export ไม่สำเร็จ');
      }
    },
    openImportDialog() {
      this.importText = '';
      this.importParseError = null;
      this.importClearTurns = true;
      this.importDialog = true;
    },
    pickImportFile() {
      const el = this.$refs.importFile;
      if (el) { el.value = ''; el.click(); }   // clearing first so re-picking the SAME file still fires change
    },
    onImportFileChosen(evt) {
      const file = evt && evt.target && evt.target.files && evt.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => { this.importText = String(reader.result || ''); };
      reader.onerror = () => { this.importParseError = 'อ่านไฟล์ไม่สำเร็จ'; };
      reader.readAsText(file);
    },
    async confirmImport() {
      const preview = this.importPreview;
      if (!preview) return;
      try {
        const res = await this.s.importForm(preview.snapshot, { clearTurns: this.importClearTurns });
        this.systemErrorText = null;
        this.importDialog = false;
        const applied = (res && res.appliedCount) || 0;
        const dropped = (res && res.droppedCount) || 0;
        const remapped = (res && res.remappedCount) || 0;
        const members = (res && res.shortFormMemberCount) || 0;
        let msg = `Import แล้ว — ฟอร์ม #${(res && res.newFormId) || 0} · ${applied} คำตอบ`;
        if (remapped) msg += ` · จับคู่ด้วย FieldKey ${remapped} ข้อ`;
        // Card boxes have no question of their own, so "N คำตอบ" alone never says whether they came back.
        if (members) msg += ` · ช่องในการ์ด ${members} ช่อง`;
        if (dropped) msg += ` · ข้าม ${dropped} ข้อ`;
        // 🔴 Dropped rows are a WARNING, not a success: the fixture that just loaded is not the
        // one that was exported, and a tester who does not notice will chase a ghost.
        if (dropped) this.showError(msg);
        else this.showSnack(msg);
        (res && res.warnings ? res.warnings : []).forEach((w) => console.warn('[tax-import]', w));
      } catch (err) {
        this.showError((err && err.message) || 'import ไม่สำเร็จ');
      }
    },

    // ── chat ──────────────────────────────────────────────────────────
    async onSend(text, formSubmit) {
      this.pendingUserMessage = text;
      this.systemErrorText = null;
      this.startElapsed();
      try {
        await this.s.send(text, formSubmit);
        this.$nextTick(() => this.scrollChatToBottom());
      } catch (err) {
        const message = (err && err.message) || 'เกิดข้อผิดพลาด';
        this.systemErrorText = message;
        this.showError(message);
      } finally {
        this.pendingUserMessage = null;
        this.stopElapsed();
      }
    },
    /**
     * A card the customer pressed — a mini-form's บันทึก / ไม่มีรายการเหล่านี้, or the
     * simulation's "แสดงผลิตภัณฑ์".
     *
     * 🔴 THE FIELD KEY COMES FROM THE CARD, and that is the whole point of this page as a
     * test surface. The backend checks every postback against "is this submission for the
     * screen the form is actually on?" — and since the envelope started carrying
     * `postback:{kind,fieldKey}`, the card is the only source a client has for it. A mobile
     * app receives a card and nothing else; it has no turn list. So reading the key off the
     * card here makes a press on this page travel exactly as a press in the app does.
     *
     * (Until that block shipped this method stamped the key from the page's own turn list, a
     * view only this page has — which is why a green run here used to prove nothing about
     * the app: the app could not name the key at all, so every postback it built was
     * rejected as "การ์ดไม่ตรงกับคำถามปัจจุบัน". That is closed, and so is the fallback that
     * outlived it.)
     *
     * 🔴 Trusting the card does NOT weaken the check. The comparison is the SERVER's, made
     * against its own idea of the current question — a client naming the wrong key is
     * refused there, whoever put the key in the payload, and the refusal is reported as
     * `card.submit → rejected` in the Flow tab with the stale key named.
     *
     * 🔴 No text. A press is not typing: the server composes the transcript line from the
     * payload it accepted, so the record says what was STORED rather than what a client
     * claimed. Same contract the mini-form postback already uses.
     */
    onCardSubmit(payload) {
      if (!this.canSend || !payload) return;

      // 🔴 THE PAYLOAD GOES OUT EXACTLY AS THE CARD BUILT IT — no key this page knows and the
      // phone does not. Two shortcuts used to live here and both made this surface answer
      // differently from production, which costs it the only job it has (owner's call,
      // 30 Aug 2026 — the same session that put both wires on one reader):
      //
      //   1. `fieldKey` fell back to `liveAskedFieldKey`, the page's own view of the turn.
      //      No app has that view: a card with no `postback` block is READ-ONLY on the phone
      //      (the block IS the capability flag — TaxShortFormCardV2), so filling the key in
      //      here made a card pressable that production would never accept a press from, and
      //      every green run proved something the app cannot do.
      //   2. A card whose key was not the live question was refused BEFORE sending. The phone
      //      sends it and lets the node decide — and the node has an answer worth reading:
      //      `card.submit → rejected` in the Flow tab, naming the stale key. Refusing locally
      //      hid exactly the failure a tester would be here to reproduce.
      //
      // What that costs: pressing a dead card now spends a turn, and the amounts typed into
      // it are re-seeded from the reply like any other submission. That is what the app does,
      // and the trace says why — which is the trade this surface exists to make.
      // 🔴 A card button that TYPES rather than posts (the summary's «บันทึกและดำเนินการต่อ»,
      //    2026-09-10): the app sends `submitButton.message` into the chat as plain text, so
      //    this page does exactly the same — the quick-reply lane, no formSubmit at all.
      if (typeof payload.message === 'string' && payload.message.trim() !== '' && !payload.fieldKey) {
        this.onSend(payload.message.trim());
        return;
      }

      if (!payload.fieldKey) return;   // …the card said it cannot be answered. It cannot.

      this.onSend('', payload);
    },
    onQuickReply(value) {
      this.onSend(value);
    },
    /**
     * T12 — a chip of the peek card behaves exactly like a chat quick reply: it
     * SENDS its `value` (the `label` is display copy). Same guard as the chat
     * panel, because a disabled chip still fires `keydown` in some browsers.
     */
    /** A8 — only ever on a NEW reply; opening an old turn must not yank the view. */
    scrollChatToBottom() {
      const el = document.querySelector('[data-testid="tpg-chat-bubbles"]');
      if (el) el.scrollTop = el.scrollHeight;
    },

    // ── timeline / inspector ──────────────────────────────────────────
    isActiveTurn(turnId) {
      return Boolean(this.activeTurn && this.activeTurn.turnId === turnId);
    },
    repeatTitle(t) {
      const key = (t && t.askedFieldKey) || '—';
      return `ถามข้อเดิม (${key}) โดยไม่มีคำตอบใดเปลี่ยนในเทิร์นนี้ — ตรวจ VisibleWhen / prompt / ตัวเลือกของข้อนี้`;
    },
    async onOpenTurn(turnId) {
      if (!turnId) return;
      await this.s.openTurn(turnId);
      this.taxJsonShowAll = false;
      // A turn that 404s must NOT be written back into the URL — that is exactly
      // the dead deep link L15 exists to clear (TC-62).
      if (this.store.turnNotFound) {
        this.$router.replace({ query: { session: this.store.sessionId } });
        return;
      }
      this.$router.replace({ query: { session: this.store.sessionId, turn: turnId } });
    },
    /** Only reachable while `turns` is non-empty, and `openTurn()` clears `turnNotFound` itself. */
    async openSessionFromStart() {
      const first = this.store.turns[0];
      if (first) await this.onOpenTurn(first.turnId);
    },

    // ── deep link / clipboard ─────────────────────────────────────────
    /** The ONE place an edit URL is assembled — never build this path anywhere else. */
    buildEditHref(ref) {
      if (!ref) return '';
      if (ref.kind === 'configVersion') return '/TaxConfigurationV2';
      const base = `/TaxQuestionV2/${ref.configVersionId}/${ref.questionVersionId}`;
      const qs = [];
      if (ref.groupCode) qs.push(`section=${encodeURIComponent(ref.groupCode)}`);
      if (ref.kind === 'questionItem' && ref.questionItemId) {
        qs.push(`item=${encodeURIComponent(ref.questionItemId)}`);
      }
      return qs.length ? `${base}?${qs.join('&')}` : base;
    },
    openEdit(ref) {
      const href = this.buildEditHref(ref);
      if (href) window.open(href, '_blank');
    },
    copyTurnLink() {
      if (!this.activeTurn) return;
      const url = `${window.location.origin}/TaxPlaygroundV2?session=${encodeURIComponent(this.store.sessionId)}&turn=${this.activeTurn.turnId}`;
      this.onCopy({ text: url, label: 'ลิงก์เทิร์น' });
    },
    /** A4 — every copyable block funnels through here, including the fallback path. */
    async onCopy(payload) {
      const text = String((payload && payload.text) ?? '');
      if (navigator.clipboard && navigator.clipboard.writeText) {
        try {
          await navigator.clipboard.writeText(text);
          this.showSnack('คัดลอกแล้ว', 1500);
          return;
        } catch (err) {
          // not https / permission denied → fall through to the manual path
        }
      }
      if (this.legacyCopy(text)) {
        this.showSnack('คัดลอกแล้ว', 1500);
        return;
      }
      this.showSnack('กด Ctrl/⌘ + C เพื่อคัดลอก', 3500);
    },
    /** Leaves the text SELECTED so `Ctrl/⌘ + C` still works when execCommand is blocked. */
    legacyCopy(text) {
      try {
        const area = document.createElement('textarea');
        area.value = text;
        area.setAttribute('readonly', 'readonly');
        area.style.position = 'fixed';
        area.style.top = '0';
        area.style.left = '0';
        area.style.opacity = '0';
        document.body.appendChild(area);
        area.select();
        const ok = document.execCommand && document.execCommand('copy');
        document.body.removeChild(area);
        return Boolean(ok);
      } catch (err) {
        return false;
      }
    },

    // ── misc ──────────────────────────────────────────────────────────
    startElapsed() {
      this.stopElapsed();
      this.elapsedSec = 0;
      this.elapsedTimer = setInterval(() => {
        this.elapsedSec += 1;
      }, 1000);
    },
    /** TC-54 — no timer may outlive the turn (or the view). */
    stopElapsed() {
      if (this.elapsedTimer) clearInterval(this.elapsedTimer);
      this.elapsedTimer = null;
      this.elapsedSec = 0;
    },
    /** เวลาของเทิร์น = **เวลาไทย** (`createdDate` เป็น UTC ไม่มี `Z` บน wire — ดู `utils/thaiTime.js`) */
    fmtTime(value) {
      return fmtThaiTime(value);
    },
    showSnack(message, timeout) {
      this.snackbarMessage = message;
      this.snackbarColor = 'success';
      this.snackbarTimeout = timeout || 3500;
      this.snackbar = true;
    },
    showError(message) {
      this.snackbarMessage = message;
      this.snackbarColor = 'error';
      this.snackbarTimeout = 3500;
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
/* ── the shell ────────────────────────────────────────────────────────────
   ONE scroll region per column, never a page-level one on top of them.

   🔴 `height: 100%`, and deliberately NOT a measured pixel value. `.v-main` is a
   `flex: 1` item of `.v-application__wrap`, so its height is definite and 100%
   resolves against its CONTENT box — i.e. minus the 64 px app-bar padding — with
   no arithmetic and nothing to keep in sync. The measured version this replaced
   read the DOM in `mounted()`, which is genuinely unwinnable: the reading is wrong
   if the browser restored a scroll offset (rect.top is shifted), wrong again while
   NavBar still has its logo/breadcrumb images in flight (`.v-main` has not shrunk
   yet — observed 36 px off), and a ResizeObserver put on the scroller to catch the
   second case did not re-fire reliably. CSS just gets it right on every frame.

   `overflow-y: auto` rather than `hidden`: on a viewport too short to hold the
   chrome plus `min-height` of the columns the shell degrades to a single page
   scrollbar instead of clipping the toolbar out of reach. At any normal size
   the children add up to exactly the height and no scrollbar appears. */
.tpg-page {
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  height: 100%;
  overflow-y: auto;
}

.tpg-container {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  padding: 12px 16px;
}

/* Chrome (toolbar, alerts, control bar, peek card, turn strip) keeps its natural
   height; only the pane below is elastic. */
.tpg-container > *:not(.tpg-row):not(.tpg-xs-pane) {
  flex: 0 0 auto;
}

.tpg-row,
.tpg-xs-pane {
  flex: 1 1 auto;
  min-height: 320px;
}

/* xs keeps its single-column, scroll-the-whole-thing behaviour — it is just bounded
   by the shell now instead of by the page. Nothing inside may be squeezed to make
   it fit; the pane scrolls instead. */
.tpg-xs-pane {
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.tpg-xs-pane > * {
  flex: 0 0 auto;
}

/* Each column scrolls on its own so chat and the form template can be compared
   without the page sliding underneath them. `height: 100%` — NOT a `calc()` off
   `100vh`: the row already knows exactly how tall it is, and any viewport-based
   guess here is what put the columns below the fold. */
.tpg-col {
  height: 100%;
  min-height: 0;
  overflow-y: auto;
}

/* ── chrome density ───────────────────────────────────────────────────────
   Both are global admin-house classes (`assets/styles/globals.css`) whose stock
   gaps — 25 px above the toolbar, 2 rem below the card — are sized for a page
   that scrolls. In a shell every one of those pixels is taken off the columns,
   so they are tightened HERE only, leaving the other ~20 admin views alone. */
.header-title {
  margin-top: 4px;
}

.card2 {
  margin-bottom: 12px;
  padding-top: 8px;
}

/* Collapsed timeline — a rail wide enough for one icon button and a vertical
   label. It stays inside the 1/12 the grid left for it (≥ 80 px at md+), so the
   11/12 given to chat + inspector can never push it onto a second line. */
/* Two classes on purpose: `.v-col-auto` also sets width/max-width, and scoped
   styles are not guaranteed to be injected after Vuetify's sheet. */
.tpg-col.tpg-col--rail {
  flex: 0 0 44px;
  width: 44px;
  min-width: 44px;
  max-width: 44px;
  overflow-x: hidden;
}

.tpg-rail-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding-top: 2px;
}

.tpg-rail-label {
  writing-mode: vertical-rl;
  font-size: 11px;
  white-space: nowrap;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.tpg-inspector {
  display: flex;
  flex-direction: column;
  overflow-y: hidden;
}

.tpg-inspector--waiting {
  opacity: 0.55;
  pointer-events: none;
}

.tpg-inspector-head {
  position: sticky;
  top: 0;
  z-index: 2;
  min-height: 40px;
  background: rgb(var(--v-theme-surface));
}

/* The ONE pane of the inspector column — it takes every pixel `.tpg-inspector-head`
   left over and scrolls on its own. (It used to share the column with a pinned
   Form Template at a 55/45 flex split; both halves ended up too short to read.) */
.tpg-pane {
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 0;
}

/* 🔴 `flex: none` — Vuetify's horizontal `.v-tabs` does NOT set it, so the moment
   this strip becomes a flex item next to tall content it gets squeezed and the
   Thai labels are clipped mid-character. Inert while `.tpg-pane` is a block. */
.tpg-tabs {
  flex: none;
  position: sticky;
  top: 0;
  z-index: 1;
  background: rgb(var(--v-theme-surface));
}

/* `flex: none` for the same reason as `.tpg-tabs` above — this strip is a flex item
   of `.tpg-xs-pane` now, and a squeezed `.v-tabs` clips Thai labels mid-character. */
.tpg-mobile-tabs {
  flex: none;
  position: sticky;
  top: 0;
  z-index: 2;
  background: rgb(var(--v-theme-surface));
}

.tpg-turn-card {
  cursor: pointer;
}

.tpg-turn--active {
  border-color: rgb(var(--v-theme-primary));
  background: rgba(30, 136, 229, 0.08);
}

.tpg-strip {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  white-space: nowrap;
  padding-bottom: 4px;
}

.tpg-strip-card {
  flex: 0 0 150px;
  width: 150px;
  padding: 6px 8px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  cursor: pointer;
}

.tpg-form-pane,
.tpg-json-pane {
  min-height: 0;
}

/* 🔴 `overflow-wrap: anywhere` — NOT `word-break: break-word/break-all`. A break
   is allowed only when the token genuinely does not fit, so `nationality` stops
   being chopped in half mid-word, while Thai (no break opportunities at all) can
   still wrap. Anything still too long scrolls INSIDE this box (`overflow: auto`)
   instead of widening the column. */
.code-block {
  font-family: monospace;
  font-size: 12px;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: normal;
  overflow-wrap: anywhere;
  background: #0f1115;
  color: #dbe2ea;
  border-radius: 6px;
  padding: 8px;
  max-height: 34vh;
  overflow: auto;
}

/* ── inspector density (the column the admin actually reads) ─────────────
   These reach into the two child panels with `:deep()` on purpose: they are
   pure presentation of THIS column's cramped width, and the panels themselves
   are also rendered on the xs/sm tab layout, where their own defaults are the
   right ones. */

/* The six status chips wrap onto a tidy second line instead of overflowing. */
.tpg-inspector :deep([data-testid='tpg-cf-counts']) {
  row-gap: 4px;
  column-gap: 4px;
  max-width: 100%;
}

.tpg-inspector :deep([data-testid='tpg-cf-counts'] .v-chip) {
  flex: 0 1 auto;
  max-width: 100%;
}

/* Form Template table: the value column gets room, the status chip gets a gutter,
   and a table too wide for the column scrolls in its OWN box (never the page). */
.tpg-inspector :deep(.cf-panels .v-expansion-panel-text__wrapper) {
  overflow-x: auto;
}

.tpg-inspector :deep(.cf-table) {
  min-width: 320px;
}

.tpg-inspector :deep(.cf-table td) {
  padding: 4px 8px;
}

.tpg-inspector :deep(.cf-label) {
  width: 36%;
}

.tpg-inspector :deep(.cf-status) {
  width: 24%;
  padding-right: 12px;
}

.tpg-inspector :deep(.cf-value) {
  width: 40%;
  word-break: normal;
  overflow-wrap: anywhere;
}

.tpg-inspector :deep(.cf-cust) {
  word-break: normal;
  overflow-wrap: anywhere;
}

/* Same mid-word rule for the dark boxes owned by the child panels. */
.tpg-inspector :deep(.code-block) {
  word-break: normal;
  overflow-wrap: anywhere;
  overflow-x: auto;
}
</style>
